import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate, Link } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { FileDown, FileText, Printer, Info, Receipt, AlertTriangle, Plus, X, ChevronDown, ChevronUp, Upload, Check, Ban, Landmark, Mail, Send, CalendarDays, Wallet, Undo2, Eye } from 'lucide-react';
import InvoicePreviewModal from '../components/InvoicePreviewModal';
import toast from 'react-hot-toast';
import api from '../api';
import { can } from '../auth';
import ExportMenu from '../components/ExportMenu';
import {
  downloadCSV, downloadExcel, downloadTablePDF, downloadBillingPDF, downloadInvoicePDF, buildInvoicePDF, downloadCreditNotePDF, printTable, STATUS_LABELS,
} from '../utils/exportHelpers';

const STATUS_COLORS = {
  created:          'bg-gray-100 text-gray-700',
  collected:        'bg-blue-100 text-blue-700',
  at_depot:         'bg-purple-100 text-purple-700',
  out_for_delivery: 'bg-yellow-100 text-yellow-700',
  delivered:        'bg-green-100 text-green-700',
  failed_delivery:  'bg-red-100 text-red-700',
  returned_to_depot:'bg-orange-100 text-orange-700',
  rescheduled:      'bg-indigo-100 text-indigo-700',
  cancelled:        'bg-red-200 text-red-900',
};

const dmy = d => new Date(d).toLocaleDateString('en-ZA');

function Explain({ children }) {
  return (
    <div className="flex items-start gap-2 bg-blue-50 border border-blue-100 rounded-xl px-4 py-3">
      <Info size={15} className="text-blue-500 flex-shrink-0 mt-0.5" />
      <p className="text-sm text-blue-900">{children}</p>
    </div>
  );
}

function SummaryCard({ label, value, colorClass }) {
  return (
    <div className={`rounded-xl p-4 ${colorClass}`}>
      <p className="text-xl font-bold">{value}</p>
      <p className="text-xs mt-0.5 opacity-75">{label}</p>
    </div>
  );
}

// ── Client Statements ─────────────────────────────────────────────────────────
const CLIENT_COLUMNS = [
  { header: 'Tracking #',  value: p => p.tracking_number,                                              width: 22, pdfWidth: 28 },
  { header: 'Description', value: p => p.description || '',                                            width: 28, pdfWidth: 34 },
  { header: 'Role',        value: p => p.client_role === 'sender' ? 'Sender' : 'Recipient',            width: 12, pdfWidth: 18 },
  { header: 'Sender',      value: p => p.sender_name || '',                                            width: 22, pdfWidth: 28 },
  { header: 'Recipient',   value: p => p.recipient_name || '',                                         width: 22, pdfWidth: 28 },
  { header: 'Status',      value: p => STATUS_LABELS[p.status] || p.status,                            width: 20, pdfWidth: 22 },
  { header: 'Driver',      value: p => p.driver_name || '',                                            width: 18, pdfWidth: 20 },
  { header: 'Price (R)',   value: p => p.final_price ? parseFloat(p.final_price).toFixed(2) : '0.00', width: 12, pdfWidth: 16, align: 'right' },
  { header: 'Date',        value: p => new Date(p.created_at).toLocaleDateString('en-ZA'),             width: 14, pdfWidth: 18 },
];

function TabClientStatements() {
  const today      = new Date().toISOString().split('T')[0];
  const monthStart = new Date(new Date().getFullYear(), new Date().getMonth(), 1).toISOString().split('T')[0];

  const [from,     setFrom]     = useState(monthStart);
  const [to,       setTo]       = useState(today);
  const [clientId, setClientId] = useState('');
  const [role,     setRole]     = useState('both');
  const [params,   setParams]   = useState(null);
  const [exporting, setExporting] = useState('');

  const { data: clients = [] } = useQuery({
    queryKey: ['clients-all'],
    queryFn: () => api.get('/clients').then(r => r.data),
  });

  const { data: report, isLoading, isFetching } = useQuery({
    queryKey: ['client-report', params],
    queryFn: () => api.get('/reports/client', { params }).then(r => r.data),
    enabled: !!params,
    staleTime: 0,
  });

  const generate = () => {
    if (!clientId) { toast.error('Please select a client'); return; }
    setParams({ client_id: clientId, from, to, role });
  };

  const quickRange = (days) => {
    setTo(new Date().toISOString().split('T')[0]);
    setFrom(new Date(Date.now() - days * 86400000).toISOString().split('T')[0]);
  };

  const handleExport = async (fmt) => {
    if (!report?.parcels?.length) { toast.error('No data to export'); return; }
    setExporting(fmt);
    try {
      const safeName = (report.client.name || 'client').replace(/[^a-z0-9]/gi, '_');
      const base = `client_report_${safeName}_${from}_to_${to}`;
      const sub  = `${report.client.name}  •  ${from} – ${to}`;

      if (fmt === 'csv')     downloadCSV(report.parcels, CLIENT_COLUMNS, `${base}.csv`);
      if (fmt === 'excel')   downloadExcel(report.parcels, CLIENT_COLUMNS, `${base}.xlsx`, 'Client Report');
      if (fmt === 'pdf')     downloadTablePDF(report.parcels, CLIENT_COLUMNS, `${base}.pdf`, 'Client Report', sub);
      if (fmt === 'billing') downloadBillingPDF(report);
      if (fmt === 'print')   printTable(report.parcels, CLIENT_COLUMNS, 'Client Report', sub);

      if (fmt !== 'print') toast.success('Downloaded');
    } catch (err) {
      toast.error('Export failed');
    } finally {
      setExporting('');
    }
  };

  return (
    <div className="space-y-4">
      <Explain>
        <strong>List one client&apos;s parcels for a period.</strong> Choose the client and the dates —
        you get every parcel they sent or received, with prices and a total, as a
        <strong> Parcel Activity PDF</strong>. This is a list of what was shipped, not their account —
        for money owed, payments and credit notes use <strong>Month-end</strong>.
      </Explain>

      {/* Controls */}
      <div className="bg-white rounded-xl shadow-sm p-4">
        <div className="flex flex-wrap gap-3 items-end">
          <div className="flex-1 min-w-52">
            <label className="block text-xs font-medium text-gray-500 mb-1">Client</label>
            <select value={clientId} onChange={e => setClientId(e.target.value)}
              className="w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-orange-400">
              <option value="">— Select client —</option>
              {clients.map(c => (
                <option key={c.id} value={c.id}>{c.name}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">Parcel role</label>
            <select value={role} onChange={e => setRole(e.target.value)}
              className="border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-orange-400">
              <option value="both">Sender &amp; Recipient</option>
              <option value="sender">As Sender only</option>
              <option value="recipient">As Recipient only</option>
            </select>
          </div>

          <div className="flex flex-col gap-1">
            <label className="text-xs font-medium text-gray-500">Quick range</label>
            <div className="flex gap-1">
              {[['This month', null], ['30d', 30], ['90d', 90]].map(([lbl, days]) => (
                <button key={lbl}
                  onClick={() => days ? quickRange(days) : (setFrom(monthStart), setTo(today))}
                  className="text-xs px-2 py-1.5 rounded border border-gray-300 text-gray-600 hover:border-orange-400 transition-colors whitespace-nowrap">
                  {lbl}
                </button>
              ))}
            </div>
          </div>

          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">From</label>
            <input type="date" value={from} onChange={e => setFrom(e.target.value)}
              className="border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-orange-400" />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">To</label>
            <input type="date" value={to} onChange={e => setTo(e.target.value)}
              className="border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-orange-400" />
          </div>

          <button onClick={generate} disabled={isLoading || isFetching}
            className="px-5 py-2 bg-orange-500 hover:bg-orange-600 text-white text-sm font-medium rounded-lg disabled:opacity-50 transition-colors">
            {isLoading || isFetching ? 'Loading…' : 'Generate Statement'}
          </button>
        </div>
      </div>

      {report && (
        <>
          <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
            <SummaryCard label="Total Parcels" value={report.summary.total}     colorClass="bg-blue-50 text-blue-700" />
            <SummaryCard label="Delivered"     value={report.summary.delivered}  colorClass="bg-green-50 text-green-700" />
            <SummaryCard label="Pending"       value={report.summary.pending}    colorClass="bg-yellow-50 text-yellow-700" />
            <SummaryCard label="Failed"        value={report.summary.failed}     colorClass="bg-red-50 text-red-700" />
            <SummaryCard
              label="Total Value (excl. cancelled)"
              value={`R${parseFloat(report.summary.total_value).toFixed(2)}`}
              colorClass="bg-orange-50 text-orange-700"
            />
          </div>

          <div className="flex gap-2 flex-wrap items-center">
            <span className="text-xs text-gray-400 font-medium">Export:</span>
            {[
              { fmt: 'csv',   label: 'CSV',       icon: <FileDown size={12} />, cls: 'border-gray-300 text-gray-600' },
              { fmt: 'excel', label: 'Excel',     icon: <FileDown size={12} />, cls: 'border-green-300 text-green-700 bg-green-50' },
              { fmt: 'pdf',   label: 'PDF Table', icon: <FileDown size={12} />, cls: 'border-red-300 text-red-700 bg-red-50' },
              { fmt: 'print', label: 'Print',     icon: <Printer  size={12} />, cls: 'border-gray-300 text-gray-600' },
            ].map(({ fmt, label, icon, cls }) => (
              <button key={fmt} onClick={() => handleExport(fmt)} disabled={!!exporting}
                className={`flex items-center gap-1 px-3 py-1.5 text-xs border rounded-lg hover:opacity-80 disabled:opacity-40 transition-opacity ${cls}`}>
                {icon} {exporting === fmt ? '…' : label}
              </button>
            ))}
            <button onClick={() => handleExport('billing')} disabled={!!exporting}
              className="flex items-center gap-1 px-4 py-1.5 text-xs bg-orange-500 hover:bg-orange-600 text-white font-semibold rounded-lg disabled:opacity-40 transition-colors ml-1">
              <FileText size={12} /> Parcel Activity PDF
            </button>
          </div>

          <div className="bg-white rounded-xl shadow-sm overflow-hidden">
            {report.parcels.length === 0 ? (
              <div className="p-8 text-center text-gray-400">No parcels in this period</div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50 text-gray-500 text-xs uppercase">
                    <tr>
                      <th className="px-4 py-3 text-left">Tracking</th>
                      <th className="px-4 py-3 text-left">Description</th>
                      <th className="px-4 py-3 text-left">Sender</th>
                      <th className="px-4 py-3 text-left">Recipient</th>
                      <th className="px-4 py-3 text-left">Status</th>
                      <th className="px-4 py-3 text-left">Driver</th>
                      <th className="px-4 py-3 text-right">Price</th>
                      <th className="px-4 py-3 text-left whitespace-nowrap">Created</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {report.parcels.map(p => (
                      <tr key={p.tracking_number} className="hover:bg-gray-50">
                        <td className="px-4 py-3">
                          <a href={`/parcels/${p.tracking_number}`}
                            className="font-mono font-semibold text-orange-500 hover:underline text-xs">
                            {p.tracking_number}
                          </a>
                          {p.client_role && (
                            <span className={`ml-1.5 text-xs px-1.5 py-0.5 rounded-full font-medium ${
                              p.client_role === 'sender' ? 'bg-blue-100 text-blue-600' : 'bg-purple-100 text-purple-600'
                            }`}>
                              {p.client_role === 'sender' ? 'S' : 'R'}
                            </span>
                          )}
                        </td>
                        <td className="px-4 py-3 text-gray-600 text-xs">{p.description || '—'}</td>
                        <td className="px-4 py-3 text-gray-600 text-xs">{p.sender_name || '—'}</td>
                        <td className="px-4 py-3 text-gray-600 text-xs">{p.recipient_name || '—'}</td>
                        <td className="px-4 py-3">
                          <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${STATUS_COLORS[p.status] || 'bg-gray-100 text-gray-600'}`}>
                            {STATUS_LABELS[p.status] || p.status}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-gray-600 text-xs">{p.driver_name || '—'}</td>
                        <td className="px-4 py-3 text-right font-medium text-gray-700 text-xs">
                          {p.final_price ? `R${parseFloat(p.final_price).toFixed(2)}` : '—'}
                        </td>
                        <td className="px-4 py-3 text-gray-400 text-xs whitespace-nowrap">
                          {new Date(p.created_at).toLocaleDateString('en-ZA')}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot className="bg-orange-50">
                    <tr>
                      <td colSpan={6} className="px-4 py-2 text-xs text-gray-500 font-medium">
                        {report.parcels.length} parcel{report.parcels.length !== 1 ? 's' : ''}
                      </td>
                      <td className="px-4 py-2 text-right text-sm font-bold text-orange-700">
                        R{parseFloat(report.summary.total_value).toFixed(2)}
                      </td>
                      <td />
                    </tr>
                  </tfoot>
                </table>
              </div>
            )}
          </div>
        </>
      )}

      {!report && !isLoading && (
        <div className="bg-white rounded-xl shadow-sm p-10 text-center text-gray-400">
          Select a client and click <span className="font-medium text-gray-600">Generate Statement</span> to view billing data
        </div>
      )}
    </div>
  );
}

// ── Unbilled parcels ──────────────────────────────────────────────────────────
const UNBILLED_COLUMNS = [
  { header: 'Tracking #', value: p => p.tracking_number,                 width: 22, pdfWidth: 28 },
  { header: 'Sender',     value: p => p.sender_name || '',               width: 24, pdfWidth: 30 },
  { header: 'Recipient',  value: p => p.recipient_name || '',            width: 24, pdfWidth: 30 },
  { header: 'Status',     value: p => STATUS_LABELS[p.status] || p.status, width: 20, pdfWidth: 24 },
  { header: 'Created',    value: p => dmy(p.created_at),                 width: 15, pdfWidth: 20 },
  { header: 'Days old',   value: p => p.age_days,                        width: 10, pdfWidth: 14, align: 'right' },
];

function TabUnbilled() {
  const [exporting, setExporting] = useState('');
  const { data: rows = [], isLoading } = useQuery({
    queryKey: ['unbilled'],
    queryFn: () => api.get('/reports/unbilled').then(r => r.data),
  });
  const deliveredUnbilled = rows.filter(r => r.status === 'delivered');

  const handleExport = (fmt) => {
    if (!rows.length) { toast.error('Nothing to export'); return; }
    setExporting(fmt);
    try {
      if (fmt === 'csv')   downloadCSV(rows, UNBILLED_COLUMNS, 'unbilled_parcels.csv');
      if (fmt === 'excel') downloadExcel(rows, UNBILLED_COLUMNS, 'unbilled_parcels.xlsx', 'Unbilled');
      if (fmt === 'pdf')   downloadTablePDF(rows, UNBILLED_COLUMNS, 'unbilled_parcels.pdf', 'Unbilled parcels', `${rows.length} parcels`);
      toast.success('Downloaded');
    } finally { setExporting(''); }
  };

  return (
    <div className="space-y-4">
      <Explain>
        <strong>Parcels without a price — this is money you may be losing.</strong> Every parcel here has no
        price entered, so it can never be billed. The red ones were already <strong>delivered</strong> without
        a price — that work was done for free. Fix a parcel by opening it and entering a price.
      </Explain>
      <div className="grid grid-cols-2 gap-3">
        <SummaryCard label="Parcels without a price" value={rows.length}
          colorClass={rows.length > 0 ? 'bg-amber-50 text-amber-700' : 'bg-green-50 text-green-700'} />
        <SummaryCard label="Already delivered — never billed" value={deliveredUnbilled.length}
          colorClass={deliveredUnbilled.length > 0 ? 'bg-red-50 text-red-700' : 'bg-green-50 text-green-700'} />
      </div>
      <div className="flex gap-2 items-center">
        <span className="text-xs text-gray-400 font-medium">Export:</span>
        {['csv', 'excel'].map(fmt => (
          <button key={fmt} onClick={() => handleExport(fmt)} disabled={!!exporting}
            className="flex items-center gap-1 px-3 py-1.5 text-xs border border-gray-300 text-gray-600 rounded-lg hover:opacity-80 disabled:opacity-40">
            <FileDown size={12} /> {fmt.toUpperCase()}
          </button>
        ))}
      </div>
      <div className="bg-white rounded-xl shadow-sm overflow-hidden">
        {isLoading ? <div className="p-8 text-center text-gray-400">Loading…</div>
        : rows.length === 0 ? <div className="p-8 text-center text-green-600 font-medium">✅ Every parcel has a price — nothing unbilled</div>
        : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 text-gray-500 text-xs uppercase">
                <tr>
                  <th className="px-4 py-3 text-left">Tracking</th>
                  <th className="px-4 py-3 text-left">Sender</th>
                  <th className="px-4 py-3 text-left">Recipient</th>
                  <th className="px-4 py-3 text-left">Status</th>
                  <th className="px-4 py-3 text-left">Created</th>
                  <th className="px-4 py-3 text-right">Days old</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {rows.map(p => (
                  <tr key={p.tracking_number} className={p.status === 'delivered' ? 'bg-red-50/60 hover:bg-red-50' : 'hover:bg-gray-50'}>
                    <td className="px-4 py-3">
                      <a href={`/parcels/${p.tracking_number}`} className="font-mono text-xs font-semibold text-orange-500 hover:underline">
                        {p.tracking_number}
                      </a>
                    </td>
                    <td className="px-4 py-3 text-gray-600 text-xs">{p.sender_name || '—'}</td>
                    <td className="px-4 py-3 text-gray-600 text-xs">{p.recipient_name || '—'}</td>
                    <td className="px-4 py-3">
                      <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${STATUS_COLORS[p.status] || 'bg-gray-100 text-gray-600'}`}>
                        {STATUS_LABELS[p.status] || p.status}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-gray-400 text-xs">{dmy(p.created_at)}</td>
                    <td className="px-4 py-3 text-right text-xs font-semibold text-gray-600">{p.age_days}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

// ── Invoices ──────────────────────────────────────────────────────────────────
const Rand = (v) => `R${parseFloat(v || 0).toFixed(2)}`;
const round2 = (n) => Math.round(n * 100) / 100;

const INVOICE_STATUS = {
  draft:     { label: 'Draft',     cls: 'bg-gray-100 text-gray-600 border border-dashed border-gray-300' },
  unpaid:    { label: 'Unpaid',    cls: 'bg-amber-100 text-amber-700' },
  part_paid: { label: 'Part-paid', cls: 'bg-blue-100 text-blue-700' },
  paid:      { label: 'Paid',      cls: 'bg-green-100 text-green-700' },
  credited:  { label: 'Credited',  cls: 'bg-purple-100 text-purple-700' },
  cancelled: { label: 'Cancelled', cls: 'bg-gray-100 text-gray-500' },
};

// Modal: credit part or all of an invoice. You cannot un-issue a tax invoice —
// a mistake is corrected by crediting it, which is what this records.
function CreditNoteModal({ invoice, onClose, onSaved }) {
  const balance = parseFloat(invoice.balance ?? invoice.total);
  const total = parseFloat(invoice.total);
  const alreadyCredited = parseFloat(invoice.credited_amount || 0);
  const paid = parseFloat(invoice.paid_amount || 0);
  const maxCredit = round2(total - alreadyCredited);

  const [amount, setAmount] = useState(balance > 0 ? balance.toFixed(2) : maxCredit.toFixed(2));
  const [reason, setReason] = useState('');
  const [release, setRelease] = useState(false);
  const [busy, setBusy] = useState(false);

  const amt = parseFloat(amount) || 0;
  const isFull = amt >= maxCredit - 0.005 && alreadyCredited + amt >= total - 0.005;
  const vat = round2(amt - amt / 1.15);

  const save = async () => {
    if (!amt || amt <= 0) { toast.error('Enter the amount to credit'); return; }
    if (!reason.trim()) { toast.error('Say why — it prints on the credit note the client gets'); return; }
    setBusy(true);
    try {
      const { data } = await api.post(`/billing/invoices/${invoice.id}/credit-notes`, {
        amount: amt, reason: reason.trim(), release_parcels: release && isFull,
      });
      toast.success(`${data.credit_note_number} issued — R${parseFloat(data.total).toFixed(2)} credited`);
      if (data.released_parcels > 0)
        toast.success(`${data.released_parcels} parcel${data.released_parcels === 1 ? '' : 's'} back on Ready to Invoice`);
      onSaved();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Could not issue that credit note');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-start p-4 bg-black/50 overflow-y-auto">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-md p-5 m-auto my-4">
        <div className="flex items-center justify-between mb-1">
          <h2 className="font-bold text-gray-800">Credit {invoice.invoice_number}</h2>
          <button onClick={onClose}><X size={18} className="text-gray-400" /></button>
        </div>
        <p className="text-xs text-gray-400 mb-4">
          {invoice.client_name} · invoiced {Rand(total)}
          {alreadyCredited > 0 && ` · ${Rand(alreadyCredited)} already credited`}
          {paid > 0 && ` · ${Rand(paid)} paid`}
        </p>

        <div className="bg-blue-50 border border-blue-100 rounded-lg px-3 py-2 mb-4">
          <p className="text-xs text-blue-900">
            An invoice the client already has can&apos;t simply be deleted. This issues a numbered
            <strong> credit note</strong> against it, showing the VAT being credited back.
          </p>
        </div>

        <label className="block text-xs font-medium text-gray-500 mb-1">Amount to credit (VAT inclusive)</label>
        <input type="number" step="0.01" min="0" max={maxCredit} value={amount}
          onChange={(e) => setAmount(e.target.value)}
          className="w-full border rounded-lg px-3 py-2 text-sm font-bold focus:outline-none focus:ring-2 focus:ring-orange-400" />
        <p className="text-xs text-gray-400 mt-1">
          Up to {Rand(maxCredit)}. Of {Rand(amt)}, VAT is {Rand(vat)}.
        </p>

        <label className="block text-xs font-medium text-gray-500 mb-1 mt-3">Why is it being credited?</label>
        <input type="text" value={reason} onChange={(e) => setReason(e.target.value)}
          placeholder="e.g. Overcharged — wrong weight band"
          className="w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-orange-400" />
        <p className="text-xs text-gray-400 mt-1">This prints on the credit note the client receives.</p>

        {isFull && (
          <label className={`flex items-start gap-2 mt-4 ${paid > 0 ? 'opacity-50' : 'cursor-pointer'}`}>
            <input type="checkbox" checked={release} disabled={paid > 0}
              onChange={(e) => setRelease(e.target.checked)} className="mt-0.5 accent-orange-500" />
            <span className="text-sm text-gray-700">
              Put its parcels back on <strong>Ready to Invoice</strong>
              <span className="block text-xs text-gray-400">
                {paid > 0
                  ? 'Not available — this invoice has payments. Remove them first.'
                  : 'For when the whole invoice was wrong (billed to the wrong client) and the parcels still need billing to someone.'}
              </span>
            </span>
          </label>
        )}

        <div className="flex justify-end gap-2 mt-5">
          <button onClick={onClose} className="px-4 py-2 text-sm text-gray-500 hover:text-gray-700">Cancel</button>
          <button onClick={save} disabled={busy}
            className="bg-purple-600 hover:bg-purple-700 text-white px-4 py-2 rounded-lg text-sm font-semibold disabled:opacity-50">
            {busy ? 'Issuing…' : `Issue credit note · ${Rand(amt)}`}
          </button>
        </div>
      </div>
    </div>
  );
}

// Modal: record a payment against one invoice
function PaymentModal({ invoice, onClose, onSaved }) {
  const balance = parseFloat(invoice.balance ?? invoice.total);
  const [amount,  setAmount]  = useState(balance.toFixed(2));
  const [method,  setMethod]  = useState('card');
  const [paidAt,  setPaidAt]  = useState(new Date().toISOString().split('T')[0]);
  const [reference, setReference] = useState('');
  const [busy,    setBusy]    = useState(false);

  const save = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/billing/invoices/${invoice.id}/payments`, {
        amount: parseFloat(amount), method,
        paid_at: paidAt, reference: reference || undefined,
      });
      toast.success(data.invoice_status === 'paid' ? '✅ Invoice fully paid' : `Payment saved — balance ${Rand(data.balance)}`);
      onSaved();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Payment failed');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-start p-4 bg-black/50 overflow-y-auto">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-md p-6 m-auto my-4">
        <div className="flex items-center justify-between mb-1">
          <h2 className="font-bold text-gray-800">Record payment — {invoice.invoice_number}</h2>
          <button onClick={onClose}><X size={20} className="text-gray-400" /></button>
        </div>
        <p className="text-xs text-gray-400 mb-4">{invoice.client_name} · balance {Rand(balance)}</p>

        <div className="space-y-3">
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Amount received (R)</label>
            <input type="number" step="0.01" min="0.01" value={amount} onChange={(e) => setAmount(e.target.value)}
              className="w-full border rounded-lg px-3 py-2 text-sm font-mono font-bold focus:outline-none focus:ring-2 focus:ring-orange-400" />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">How was it paid?</label>
            <div className="grid grid-cols-3 gap-2">
              {[['card', '💳 Card'], ['cash', '💵 Cash'], ['eft', '🏦 EFT']].map(([m, lbl]) => (
                <button key={m} type="button" onClick={() => setMethod(m)}
                  className={`py-2 rounded-lg text-sm font-medium border transition-colors ${
                    method === m ? 'bg-orange-500 text-white border-orange-500' : 'border-gray-200 text-gray-600 hover:border-orange-400'
                  }`}>
                  {lbl}
                </button>
              ))}
            </div>
            {method === 'card' && <p className="text-[11px] text-gray-400 mt-1">Capitec card machine at the office</p>}
            {method === 'eft'  && <p className="text-[11px] text-gray-400 mt-1">Into the Capitec business account</p>}
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Date paid</label>
              <input type="date" value={paidAt} onChange={(e) => setPaidAt(e.target.value)}
                className="w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-orange-400" />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Reference (optional)</label>
              <input type="text" value={reference} onChange={(e) => setReference(e.target.value)} placeholder="EFT ref / slip no."
                className="w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-orange-400" />
            </div>
          </div>
          <button onClick={save} disabled={busy || !parseFloat(amount)}
            className="w-full bg-green-600 hover:bg-green-700 text-white py-2.5 rounded-lg text-sm font-bold disabled:opacity-50">
            {busy ? 'Saving…' : `Save payment of ${Rand(amount)}`}
          </button>
        </div>
      </div>
    </div>
  );
}

// Expanded detail under an invoice row
// ── Invoice lines: parcels + extra items, each with its own discount ─────────
// Prices are VAT-inclusive. A line's discount is a percentage or a rand amount
// off THAT line (for an item: off qty × unit price). The invoice total is the
// sum of the discounted lines and VAT is carved out of that — the same
// arithmetic as the API. "Apply to all lines" is just a shortcut that sets
// every line; any line can be cleared again afterwards.
const num = (v) => { const n = parseFloat(v); return Number.isFinite(n) ? n : 0; };
const newKey = () => Math.random().toString(36).slice(2);
const newItem = (p = {}) => ({
  key: newKey(),
  description: p.name || '',
  quantity: '1',
  unit_price: p.unit_price != null ? parseFloat(p.unit_price).toFixed(2) : '',
  discount_type: '',
  discount_value: '',
});
const NO_DISCOUNT = { type: '', value: '' };

const lineDiscountAmount = (price, d) => {
  if (!d || !d.type) return 0;
  const v = num(d.value);
  return d.type === 'percent' ? price * Math.min(v, 100) / 100 : Math.min(v, price);
};
const fmtDiscount = (d) => (!d?.type ? '' : d.type === 'percent' ? `${num(d.value)}%` : `R${num(d.value).toFixed(2)}`);

// The price a parcel line will carry: a change made in the editor (from the
// price list or typed), else what the parcel is priced at now.
const effectivePrice = (p, parcelPrices) => {
  const o = parcelPrices?.[p.tracking_number];
  if (o && o.price !== '' && o.price != null) return num(o.price);
  return num(p.price);
};
const parcelPricesForApi = (pp, trackingNumbers) => Object.fromEntries(
  trackingNumbers
    .filter((t) => pp[t] && num(pp[t].price) > 0)
    .map((t) => [t, pp[t].tier_id ? { price_tier_id: pp[t].tier_id } : { manual_price: num(pp[t].price) }])
);

// "Preview invoice": the PDF as it would print from what is on screen now,
// saved or not. Builds the same shape GET /billing/invoices/:id returns.
async function openInvoicePreview(base, { parcels, parcelDiscounts, parcelPrices = {}, items, discountNote, vatRate = 15 }, settings) {
  const lines = parcels.map((p) => {
    const price = effectivePrice(p, parcelPrices);
    const d = parcelDiscounts[p.tracking_number];
    const disc = lineDiscountAmount(price, d);
    return { ...p, price, line_discount_type: disc > 0 ? d.type : null, line_discount_value: disc > 0 ? num(d.value) : null,
      line_discount_amount: disc, net_amount: price - disc };
  });
  const its = items.filter((it) => it.description.trim() || num(it.unit_price) > 0).map((it) => {
    const amount = num(it.quantity) * num(it.unit_price);
    const d = { type: it.discount_type, value: it.discount_value };
    const disc = lineDiscountAmount(amount, d);
    return { description: it.description, quantity: num(it.quantity) || 1, unit_price: num(it.unit_price), amount,
      line_discount_type: disc > 0 ? d.type : null, line_discount_value: disc > 0 ? num(d.value) : null,
      line_discount_amount: disc, net_amount: amount - disc };
  });
  const extraItems = parcels.flatMap((p) => (p.extras || []).map((e) => ({
    description: `${e.description} — ${p.tracking_number}`, quantity: num(e.quantity), unit_price: num(e.unit_price),
    amount: num(e.amount), line_discount_type: null, line_discount_value: null, line_discount_amount: 0, net_amount: num(e.amount),
  })));
  its.push(...extraItems);
  const t = invoicePreview({ parcels, parcelDiscounts, parcelPrices, items, vatRate });
  const dates = lines.map((l) => new Date(l.created_at)).filter((x) => !isNaN(x));
  const inv = {
    status: 'draft', created_at: new Date().toISOString(), vat_rate: vatRate,
    ...base,
    lines, items: its,
    subtotal: t.subtotal, vat_amount: t.vat, total: t.total, discount_amount: t.discount, discount_note: discountNote || null,
    period_from: dates.length ? new Date(Math.min(...dates)) : null, period_to: dates.length ? new Date(Math.max(...dates)) : null,
  };
  const doc = await buildInvoicePDF(inv, settings || {});
  const url = doc.output('bloburl');
  if (!window.open(url, '_blank')) doc.save('invoice-preview.pdf');   // pop-up blocked → download instead
}

function invoicePreview({ parcels, parcelDiscounts, parcelPrices = {}, items, vatRate = 15 }) {
  const parcelLines = parcels.map((p) => {
    const price = effectivePrice(p, parcelPrices);
    const disc = lineDiscountAmount(price, parcelDiscounts[p.tracking_number]);
    return { price, disc, net: price - disc };
  });
  const itemLines = items.map((it) => {
    const price = num(it.quantity) * num(it.unit_price);
    const disc = lineDiscountAmount(price, { type: it.discount_type, value: it.discount_value });
    return { price, disc, net: price - disc };
  });
  // Items sold with the parcels themselves ride along at full price
  const extrasTotal = parcels.reduce((s, p) => s + (p.extras || []).reduce((a, e) => a + num(e.amount), 0), 0);
  const parcelsTotal = parcelLines.reduce((s, l) => s + l.price, 0);
  const itemsTotal = itemLines.reduce((s, l) => s + l.price, 0) + extrasTotal;
  const discount = [...parcelLines, ...itemLines].reduce((s, l) => s + l.disc, 0);
  const total = parcelsTotal + itemsTotal - discount;
  const subtotal = total / (1 + vatRate / 100);
  return { parcelsTotal, itemsTotal, gross: parcelsTotal + itemsTotal, discount, total, subtotal, vat: total - subtotal };
}

// To and from the API shapes
const itemsForApi = (items) => items
  .filter((it) => it.description.trim() || num(it.unit_price) > 0)
  .map((it) => ({
    description: it.description.trim(), quantity: num(it.quantity) || 1, unit_price: num(it.unit_price),
    discount_type: it.discount_type && num(it.discount_value) > 0 ? it.discount_type : null,
    discount_value: it.discount_type && num(it.discount_value) > 0 ? num(it.discount_value) : null,
  }));
const parcelDiscountsForApi = (pd, trackingNumbers) => Object.fromEntries(
  trackingNumbers
    .filter((t) => pd[t]?.type && num(pd[t].value) > 0)
    .map((t) => [t, { type: pd[t].type, value: num(pd[t].value) }])
);
const itemsFromApi = (items = []) => items.filter((it) => !it.parcel_extra_id).map((it) => ({
  key: it.id || newKey(), description: it.description,
  quantity: String(parseFloat(it.quantity)), unit_price: parseFloat(it.unit_price).toFixed(2),
  discount_type: it.line_discount_type || '',
  discount_value: it.line_discount_value != null ? String(parseFloat(it.line_discount_value)) : '',
}));
const parcelDiscountsFromApi = (lines = []) => Object.fromEntries(
  lines.filter((l) => l.line_discount_type)
    .map((l) => [l.tracking_number, { type: l.line_discount_type, value: String(parseFloat(l.line_discount_value)) }])
);

const INP = 'border rounded-lg px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-orange-400';

// One line's discount: None / % / R + the value
function LineDiscount({ value, onChange, price }) {
  const d = value || NO_DISCOUNT;
  const amt = lineDiscountAmount(num(price), d);
  return (
    <span className="inline-flex items-center gap-1">
      <select value={d.type} className={`${INP} py-1 text-xs`}
        onChange={(e) => onChange(e.target.value ? { type: e.target.value, value: d.value } : null)}>
        <option value="">No discount</option>
        <option value="percent">% off</option>
        <option value="amount">R off</option>
      </select>
      {d.type && (
        <>
          <input type="number" min="0" step={d.type === 'percent' ? '0.5' : '0.01'} max={d.type === 'percent' ? 100 : undefined}
            value={d.value} onChange={(e) => onChange({ type: d.type, value: e.target.value })}
            placeholder={d.type === 'percent' ? '10' : '20.00'} className={`${INP} py-1 text-xs w-20 text-right`} />
          {amt > 0 && <span className="text-[11px] text-red-600 whitespace-nowrap">−{Rand(amt)}</span>}
        </>
      )}
    </span>
  );
}

// The editor used for a new invoice and for a draft: parcels to tick, extra
// items, a discount per line, apply-to-all, and the running totals.
function InvoiceLinesEditor({ parcels, picked, setPicked, parcelDiscounts, setParcelDiscounts, items, setItems,
  discountNote, setDiscountNote, vatRate = 15, parcelPrices = {}, setParcelPrices, onPreview }) {
  const { data: products = [] } = useQuery({
    queryKey: ['billing-products'],
    queryFn: () => api.get('/billing/products').then((r) => r.data),
  });
  // The parcel price list, to re-price a parcel without leaving the invoice
  const { data: tiers = [] } = useQuery({
    queryKey: ['price-tiers'],
    queryFn: () => api.get('/price-tiers').then((r) => r.data),
    staleTime: 5 * 60 * 1000,
  });
  const [bulk, setBulk] = useState({ type: 'percent', value: '' });
  const pickedParcels = parcels.filter((p) => picked[p.tracking_number]);
  const preview = invoicePreview({ parcels: pickedParcels, parcelDiscounts, parcelPrices, items, vatRate });
  const setParcelPrice = (t, v) => setParcelPrices?.((m) => { const n = { ...m }; if (v) n[t] = v; else delete n[t]; return n; });

  const setItem = (key, patch) => setItems((list) => list.map((it) => (it.key === key ? { ...it, ...patch } : it)));
  const removeItem = (key) => setItems((list) => list.filter((it) => it.key !== key));
  const setParcelDiscount = (t, d) => setParcelDiscounts((m) => { const n = { ...m }; if (d) n[t] = d; else delete n[t]; return n; });

  const applyAll = () => {
    if (!(num(bulk.value) > 0)) { toast.error('Enter the discount to apply'); return; }
    const d = { type: bulk.type, value: bulk.value };
    setParcelDiscounts(Object.fromEntries(pickedParcels.map((p) => [p.tracking_number, { ...d }])));
    setItems((list) => list.map((it) => ({ ...it, discount_type: d.type, discount_value: d.value })));
    toast.success(`${fmtDiscount(d)} applied to ${pickedParcels.length + items.length} line${pickedParcels.length + items.length === 1 ? '' : 's'}`);
  };
  const clearAll = () => {
    setParcelDiscounts({});
    setItems((list) => list.map((it) => ({ ...it, discount_type: '', discount_value: '' })));
  };
  const linesWithDiscount = pickedParcels.filter((p) => parcelDiscounts[p.tracking_number]?.type).length
    + items.filter((it) => it.discount_type).length;

  return (
    <div className="space-y-4">
      {/* Apply-to-all */}
      <div className="flex flex-wrap items-center gap-2 bg-gray-50 border rounded-lg px-3 py-2">
        <span className="text-xs font-medium text-gray-600">Discount on every line:</span>
        <select value={bulk.type} onChange={(e) => setBulk((b) => ({ ...b, type: e.target.value }))} className={`${INP} py-1 text-xs`}>
          <option value="percent">% off</option>
          <option value="amount">R off each line</option>
        </select>
        <input type="number" min="0" step={bulk.type === 'percent' ? '0.5' : '0.01'} value={bulk.value}
          onChange={(e) => setBulk((b) => ({ ...b, value: e.target.value }))}
          placeholder={bulk.type === 'percent' ? '50' : '20.00'} className={`${INP} py-1 text-xs w-24 text-right`} />
        <button type="button" onClick={applyAll}
          className="text-xs font-semibold bg-white border border-orange-300 text-orange-700 hover:bg-orange-50 px-3 py-1 rounded-lg">
          Apply to all lines
        </button>
        {linesWithDiscount > 0 && (
          <button type="button" onClick={clearAll} className="text-xs text-gray-500 hover:text-red-600 hover:underline">
            Clear all discounts ({linesWithDiscount})
          </button>
        )}
        <span className="text-[11px] text-gray-400 w-full sm:w-auto sm:ml-auto">
          Then change or remove the discount on any single line below.
        </span>
      </div>

      {/* Parcels */}
      <div>
        <div className="flex items-center justify-between mb-1">
          <p className="text-xs font-medium text-gray-500">
            {parcels.length === 0
              ? 'No priced parcels waiting for this client — you can still invoice extra items.'
              : 'Tick the parcels to put on this invoice:'}
          </p>
          {parcels.length > 1 && (
            <button type="button" onClick={() => setPicked(Object.fromEntries(parcels.map((p) => [p.tracking_number, true])))}
              className="text-xs text-orange-500 hover:underline">Select all ({parcels.length})</button>
          )}
        </div>
        {parcels.length > 0 && (
          <div className="border rounded-lg divide-y max-h-80 overflow-auto">
            {parcels.map((p) => {
              const on = !!picked[p.tracking_number];
              const d = parcelDiscounts[p.tracking_number];
              const price = effectivePrice(p, parcelPrices);
              const pp = parcelPrices[p.tracking_number];
              const net = price - (on ? lineDiscountAmount(price, d) : 0);
              return (
                <div key={p.tracking_number} className={`flex flex-wrap items-center gap-x-3 gap-y-1 px-3 py-2 text-sm ${on ? '' : 'opacity-60'}`}>
                  <label className="flex items-center gap-2 cursor-pointer flex-1 min-w-[16rem]">
                    <input type="checkbox" checked={on}
                      onChange={(e) => setPicked((prev) => ({ ...prev, [p.tracking_number]: e.target.checked }))}
                      className="accent-orange-500" />
                    <span className="font-mono text-xs font-semibold">{p.tracking_number}</span>
                    {p.status && p.status !== 'delivered' && <StatusChip status={p.status} />}
                    <span className="text-gray-500 text-xs">{p.sender_name} → {p.recipient_name}</span>
                    <span className="text-gray-400 text-xs">{new Date(p.created_at).toLocaleDateString('en-ZA')}</span>
                  </label>
                  {on && setParcelPrices && (
                    <span className="flex items-center gap-1" title="Change this parcel's price — pick from the price list or type an amount">
                      <select value={pp?.tier_id || (pp ? 'custom' : '')} className={`${INP} py-1 text-xs`}
                        onChange={(e) => {
                          const v = e.target.value;
                          if (!v) return setParcelPrice(p.tracking_number, null);
                          if (v === 'custom') return setParcelPrice(p.tracking_number, { price: pp?.price ?? num(p.price).toFixed(2), tier_id: null });
                          const tier = tiers.find((x) => x.id === v);
                          setParcelPrice(p.tracking_number, { price: num(tier.price).toFixed(2), tier_id: tier.id });
                        }}>
                        <option value="">Price as booked</option>
                        {tiers.map((tier) => <option key={tier.id} value={tier.id}>{tier.name} — {Rand(tier.price)}</option>)}
                        <option value="custom">Own amount…</option>
                      </select>
                      {pp && !pp.tier_id && (
                        <input type="number" min="0" step="0.01" value={pp.price} placeholder="0.00"
                          onChange={(e) => setParcelPrice(p.tracking_number, { price: e.target.value, tier_id: null })}
                          className={`${INP} py-1 text-xs w-24 text-right`} />
                      )}
                    </span>
                  )}
                  {on && <LineDiscount value={d} price={price} onChange={(nd) => setParcelDiscount(p.tracking_number, nd)} />}
                  <span className="text-xs text-right w-28">
                    {on && pp && num(pp.price) !== num(p.price) && <span className="text-gray-400 line-through mr-1">{Rand(p.price)}</span>}
                    {on && d?.type && num(d.value) > 0 && <span className="text-gray-400 line-through mr-1">{Rand(price)}</span>}
                    <span className="font-semibold">{Rand(on ? net : price)}</span>
                  </span>
                  {(p.extras || []).length > 0 && (
                    <div className="w-full pl-6 text-[11px] text-gray-500 space-y-0.5">
                      {p.extras.map((e) => (
                        <div key={e.id} className="flex justify-between gap-2">
                          <span>+ {e.description}{num(e.quantity) !== 1 ? ` (${num(e.quantity)} × ${Rand(e.unit_price)})` : ''} <span className="text-gray-400">· sold with the parcel</span></span>
                          <span className="w-28 text-right">{Rand(e.amount)}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Extra items */}
      <div>
        <div className="flex flex-wrap items-center justify-between gap-2 mb-1">
          <p className="text-xs font-medium text-gray-500">
            Extra items — Fastway labels, packaging, anything sold besides deliveries (prices incl. VAT; a rand discount is off the whole line)
          </p>
          <div className="flex items-center gap-3">
            {products.length > 0 && (
              <select value="" className={INP}
                onChange={(e) => { const p = products.find((x) => x.id === e.target.value); if (p) setItems((l) => [...l, newItem(p)]); }}>
                <option value="">Add from price list…</option>
                {products.map((p) => <option key={p.id} value={p.id}>{p.name} — {Rand(p.unit_price)}</option>)}
              </select>
            )}
            <button type="button" onClick={() => setItems((l) => [...l, newItem()])}
              className="text-xs font-semibold text-orange-600 hover:underline whitespace-nowrap">+ Blank item</button>
          </div>
        </div>
        {items.length > 0 && (
          <div className="border rounded-lg divide-y">
            {items.map((it) => {
              const gross = num(it.quantity) * num(it.unit_price);
              const d = { type: it.discount_type, value: it.discount_value };
              return (
                <div key={it.key} className="flex flex-wrap items-center gap-2 px-3 py-2">
                  <input className={`${INP} flex-1 min-w-[12rem]`} placeholder="e.g. Fastway label — local"
                    value={it.description} onChange={(e) => setItem(it.key, { description: e.target.value })} />
                  <input className={`${INP} w-16 text-right`} type="number" min="0" step="1" title="Quantity"
                    value={it.quantity} onChange={(e) => setItem(it.key, { quantity: e.target.value })} />
                  <span className="text-xs text-gray-400">×</span>
                  <input className={`${INP} w-24 text-right`} type="number" min="0" step="0.01" placeholder="0.00" title="Unit price incl. VAT"
                    value={it.unit_price} onChange={(e) => setItem(it.key, { unit_price: e.target.value })} />
                  <LineDiscount value={d.type ? d : null} price={gross}
                    onChange={(nd) => setItem(it.key, { discount_type: nd?.type || '', discount_value: nd ? nd.value : '' })} />
                  <span className="text-xs text-right w-28">
                    {d.type && num(d.value) > 0 && <span className="text-gray-400 line-through mr-1">{Rand(gross)}</span>}
                    <span className="font-semibold">{Rand(gross - lineDiscountAmount(gross, d))}</span>
                  </span>
                  <button type="button" onClick={() => removeItem(it.key)} title="Remove item"
                    className="text-gray-300 hover:text-red-500"><X size={14} /></button>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Note + totals */}
      <div className="flex flex-wrap items-center gap-3">
        {preview.discount > 0 && (
          <input value={discountNote} onChange={(e) => setDiscountNote(e.target.value)}
            placeholder="Reason for the discount, printed on the invoice (optional)" className={`${INP} flex-1 min-w-64`} />
        )}
        {onPreview && (
          <button type="button" onClick={() => onPreview().catch(() => toast.error('Could not build the preview'))}
            title="Open the invoice as a PDF exactly as it will print, including unsaved changes"
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold bg-white border border-gray-300 text-gray-700 hover:bg-gray-50">
            <FileText size={13} /> Preview invoice
          </button>
        )}
        <p className="text-xs text-gray-600 ml-auto">
          {pickedParcels.length} parcel{pickedParcels.length !== 1 ? 's' : ''} {Rand(preview.parcelsTotal)}
          {items.length > 0 && <> + {items.length} item{items.length !== 1 ? 's' : ''} {Rand(preview.itemsTotal)}</>}
          {preview.discount > 0 && <span className="text-red-600"> − discount {Rand(preview.discount)}</span>}
          {' · '}excl. VAT {Rand(preview.subtotal)} + VAT {Rand(preview.vat)} ={' '}
          <strong className="text-gray-900">{Rand(preview.total)}</strong>
        </p>
      </div>
    </div>
  );
}

// ── Emailing a tax invoice ────────────────────────────────────────────────────
// The PDF is built here, exactly as the download, and posted to the API which
// sends it and logs the send. Nothing is emailed without this button.
function EmailInvoiceModal({ invoice, settings, onClose, onSent }) {
  const [to, setTo] = useState(invoice.client_email || '');
  const [busy, setBusy] = useState(false);
  const send = async () => {
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(to.trim())) { toast.error('Enter a valid email address'); return; }
    setBusy(true);
    try {
      // A list row has no lines — fetch the full invoice so the PDF is complete
      const full = invoice.lines ? invoice : (await api.get(`/billing/invoices/${invoice.id}`)).data;
      const doc = await buildInvoicePDF(full, settings || {});
      const fd = new FormData();
      fd.append('to', to.trim());
      fd.append('file', doc.output('blob'), `${invoice.invoice_number}.pdf`);
      const { data } = await api.post(`/billing/invoices/${invoice.id}/send`, fd);
      toast.success(`${invoice.invoice_number} emailed to ${data.sent_to}`);
      onSent?.();
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Could not send the invoice');
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-start p-4 overflow-y-auto" onClick={onClose}>
      <div className="bg-white rounded-xl shadow-xl w-full max-w-md p-5 space-y-3 m-auto my-4" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between">
          <h3 className="font-semibold text-gray-800">Email {invoice.invoice_number}</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600"><X size={18} /></button>
        </div>
        <p className="text-xs text-gray-500">
          {invoice.client_name} · {Rand(invoice.total)}
          {invoice.due_date ? ` · due ${new Date(invoice.due_date).toLocaleDateString('en-ZA')}` : ''}
        </p>
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">Send to</label>
          <input type="email" value={to} onChange={(e) => setTo(e.target.value)} placeholder="client@example.com"
            className={`${INP} w-full`} />
          {!invoice.client_email && (
            <p className="text-[11px] text-amber-600 mt-1">This client has no email on file — the address you type is used for this send only.</p>
          )}
        </div>
        <p className="text-xs text-gray-500">
          The tax invoice PDF is attached, with the amount, due date, payment reference and banking details in the message.
          The send is recorded against the invoice.
        </p>
        <div className="flex justify-end gap-2 pt-1">
          <button onClick={onClose} className="px-4 py-2 rounded-lg text-sm border text-gray-600">Cancel</button>
          <button onClick={send} disabled={busy}
            className="flex items-center gap-1.5 bg-orange-500 hover:bg-orange-600 text-white px-4 py-2 rounded-lg text-sm font-semibold disabled:opacity-50">
            <Send size={14} /> {busy ? 'Sending…' : 'Send invoice'}
          </button>
        </div>
      </div>
    </div>
  );
}

// WhatsApp the invoice: the PDF is built here, stored by the API, and the
// message (amount, due date, PDF link, live statement link, payment reference)
// opens in WhatsApp for the office to press Send.
function WhatsappInvoiceModal({ invoice, settings, onClose, onSent }) {
  const [to, setTo] = useState(invoice.client_phone || '');
  const [busy, setBusy] = useState(false);
  const send = async () => {
    if (to.replace(/\D/g, '').length < 9) { toast.error('Enter a valid phone number'); return; }
    setBusy(true);
    try {
      const full = invoice.lines ? invoice : (await api.get(`/billing/invoices/${invoice.id}`)).data;
      const doc = await buildInvoicePDF(full, settings || {});
      const fd = new FormData();
      fd.append('to', to.trim());
      fd.append('file', doc.output('blob'), `${invoice.invoice_number}.pdf`);
      const { data } = await api.post(`/billing/invoices/${invoice.id}/whatsapp`, fd);
      window.open(data.url, '_blank');
      toast.success(`WhatsApp opened for ${data.to} — press Send there`);
      onSent?.();
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Could not prepare the WhatsApp');
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-start p-4 overflow-y-auto" onClick={onClose}>
      <div className="bg-white rounded-xl shadow-xl w-full max-w-md p-5 space-y-3 m-auto my-4" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between">
          <h3 className="font-semibold text-gray-800">WhatsApp {invoice.invoice_number}</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600"><X size={18} /></button>
        </div>
        <p className="text-xs text-gray-500">
          {invoice.client_name} · {Rand(invoice.total)}
          {invoice.due_date ? ` · due ${new Date(invoice.due_date).toLocaleDateString('en-ZA')}` : ''}
        </p>
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">WhatsApp number</label>
          <input type="tel" value={to} onChange={(e) => setTo(e.target.value)} placeholder="082 123 4567"
            className={`${INP} w-full`} />
          {!invoice.client_phone && (
            <p className="text-[11px] text-amber-600 mt-1">This client has no phone number on file — the number you type is used for this send only.</p>
          )}
        </div>
        <p className="text-xs text-gray-500">
          WhatsApp opens with the message ready: the amount and due date, a link to the invoice PDF, the client&apos;s
          live statement link, the payment reference and banking details. You press Send in WhatsApp. The send is
          recorded against the invoice.
        </p>
        <div className="flex justify-end gap-2 pt-1">
          <button onClick={onClose} className="px-4 py-2 rounded-lg text-sm border text-gray-600">Cancel</button>
          <button onClick={send} disabled={busy}
            className="flex items-center gap-1.5 bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg text-sm font-semibold disabled:opacity-50">
            <Send size={14} /> {busy ? 'Preparing…' : 'Open WhatsApp'}
          </button>
        </div>
      </div>
    </div>
  );
}

const dmyt = (d) => (d ? new Date(d).toLocaleString('en-ZA', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '');

// A draft, opened: the same editor as a new invoice, plus Save / Issue / Delete
function DraftEditor({ inv, onChanged, onGone, onDone }) {
  const issued = inv.status !== 'draft';   // correcting an issued invoice nobody has seen yet
  const { data: billSettings } = useQuery({
    queryKey: ['billing-settings'],
    queryFn: () => api.get('/settings/billing').then((r) => r.data),
  });
  const qc = useQueryClient();
  const { data: uninvoiced = [] } = useQuery({
    queryKey: ['uninvoiced', inv.client_id],
    queryFn: () => api.get('/billing/uninvoiced', { params: { client_id: inv.client_id } }).then((r) => r.data),
  });
  // Parcels already on the draft + the client's other invoiceable parcels
  const parcels = [
    ...inv.lines,
    ...uninvoiced.filter((p) => !inv.lines.some((l) => l.tracking_number === p.tracking_number)),
  ];
  const [picked, setPicked] = useState(() => Object.fromEntries(inv.lines.map((l) => [l.tracking_number, true])));
  const [parcelDiscounts, setParcelDiscounts] = useState(() => parcelDiscountsFromApi(inv.lines));
  const [items, setItems] = useState(() => itemsFromApi(inv.items));
  const [discountNote, setDiscountNote] = useState(inv.discount_note || '');
  const [parcelPrices, setParcelPrices] = useState({});   // re-priced parcels, until saved
  const [busy, setBusy] = useState(null);

  const body = () => {
    const tracking = parcels.filter((p) => picked[p.tracking_number]).map((p) => p.tracking_number);
    return {
      tracking_numbers: tracking,
      items: itemsForApi(items),
      parcel_discounts: parcelDiscountsForApi(parcelDiscounts, tracking),
      parcel_prices: parcelPricesForApi(parcelPrices, tracking),
      discount_note: discountNote,
    };
  };
  const refresh = () => {
    qc.invalidateQueries({ queryKey: ['invoice', inv.id] });
    qc.invalidateQueries({ queryKey: ['uninvoiced'] });
    qc.invalidateQueries({ queryKey: ['ready-to-invoice'] });
    onChanged();
  };
  const save = async (thenIssue = false) => {
    setBusy(thenIssue ? 'issue' : 'save');
    try {
      await api.put(`/billing/invoices/${inv.id}`, body());
      setParcelPrices({});   // now written to the parcels themselves
      if (thenIssue) {
        const { data } = await api.post(`/billing/invoices/${inv.id}/issue`);
        toast.success(`Issued as ${data.invoice_number}`);
      } else {
        toast.success(issued ? `${inv.invoice_number} updated` : 'Draft saved');
      }
      refresh();
      if (issued) onDone?.();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Could not save the draft');
    } finally {
      setBusy(null);
    }
  };
  const remove = async () => {
    if (!window.confirm('Delete this draft? Its parcels go back to the invoiceable pool. Nothing is numbered yet, so no gap is left.')) return;
    setBusy('delete');
    try {
      await api.delete(`/billing/invoices/${inv.id}`);
      toast.success('Draft deleted');
      refresh();
      onGone?.();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Could not delete the draft');
      setBusy(null);
    }
  };

  return (
    <div className="space-y-3">
      {issued ? (
        <p className="text-xs text-gray-600">
          <strong className="text-gray-700">Correcting {inv.invoice_number}</strong> — it keeps its number and date.
          Once a PDF is printed or it is emailed, it is fixed. A parcel&apos;s price can be changed on its line below.
        </p>
      ) : (
        <p className="text-xs text-gray-500">
          <strong className="text-gray-700">Draft</strong> — prepared {dmyt(inv.created_at)}{inv.created_by_name ? ` by ${inv.created_by_name}` : ''}.
          Nothing is numbered or sent until you press <strong>Issue</strong>. A parcel&apos;s price can be changed on its line below.
        </p>
      )}
      <InvoiceLinesEditor parcels={parcels} picked={picked} setPicked={setPicked}
        parcelDiscounts={parcelDiscounts} setParcelDiscounts={setParcelDiscounts}
        items={items} setItems={setItems} discountNote={discountNote} setDiscountNote={setDiscountNote}
        parcelPrices={parcelPrices} setParcelPrices={setParcelPrices}
        vatRate={parseFloat(inv.vat_rate) || 15}
        onPreview={() => openInvoicePreview(inv, {
          parcels: parcels.filter((p) => picked[p.tracking_number]), parcelDiscounts, parcelPrices, items, discountNote,
          vatRate: parseFloat(inv.vat_rate) || 15 }, billSettings)} />
      {can('billing.invoice') && issued && <div className="flex flex-wrap gap-2">
        <button onClick={() => save(false)} disabled={!!busy}
          className="flex items-center gap-1.5 px-4 py-2 rounded-lg text-xs font-semibold bg-orange-500 hover:bg-orange-600 text-white disabled:opacity-50">
          <Check size={13} /> {busy === 'save' ? 'Saving…' : 'Save changes'}
        </button>
        <button onClick={() => onDone?.()} disabled={!!busy}
          className="px-4 py-2 rounded-lg text-xs font-semibold bg-white border border-gray-300 text-gray-700 hover:bg-gray-50 disabled:opacity-50">
          Done
        </button>
      </div>}
      {can('billing.invoice') && !issued && <div className="flex flex-wrap gap-2">
        <button onClick={() => save(false)} disabled={!!busy}
          className="px-4 py-2 rounded-lg text-xs font-semibold bg-white border border-gray-300 text-gray-700 hover:bg-gray-50 disabled:opacity-50">
          {busy === 'save' ? 'Saving…' : 'Save draft'}
        </button>
        <button onClick={() => save(true)} disabled={!!busy}
          title="Save, take the next invoice number and lock the invoice"
          className="flex items-center gap-1.5 px-4 py-2 rounded-lg text-xs font-semibold bg-orange-500 hover:bg-orange-600 text-white disabled:opacity-50">
          <Check size={13} /> {busy === 'issue' ? 'Issuing…' : inv.invoice_number ? `Issue as ${inv.invoice_number}` : 'Issue invoice'}
        </button>
        <button onClick={remove} disabled={!!busy} className="ml-auto text-xs text-gray-400 hover:text-red-500 hover:underline disabled:opacity-50">
          Delete draft
        </button>
      </div>}
    </div>
  );
}

function InvoiceDetail({ invoiceId, onChanged, onCredit, onEmail, onWhatsapp, onGone }) {
  const qc = useQueryClient();
  const [editing, setEditing] = useState(false);   // correcting an issued, unseen invoice
  const [previewOpen, setPreviewOpen] = useState(false);   // the in-page look at the PDF
  const { data: inv, isLoading } = useQuery({
    queryKey: ['invoice', invoiceId],
    queryFn: () => api.get(`/billing/invoices/${invoiceId}`).then((r) => r.data),
    staleTime: 0,
  });
  const { data: billSettings } = useQuery({
    queryKey: ['billing-settings'],
    queryFn: () => api.get('/settings/billing').then((r) => r.data),
  });

  const removePayment = async (pid) => {
    if (!window.confirm('Delete this payment? The invoice balance will go back up.')) return;
    try {
      await api.delete(`/billing/payments/${pid}`);
      toast.success('Payment deleted');
      qc.invalidateQueries({ queryKey: ['invoice', invoiceId] });
      onChanged();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to delete payment');
    }
  };

  if (isLoading || !inv) return <div className="p-4 text-center text-gray-400 text-sm">Loading…</div>;

  if (inv.status === 'draft' || (editing && inv.editable)) {
    return (
      <div className="bg-gray-50 border-t p-4">
        <DraftEditor inv={inv} onChanged={onChanged} onGone={onGone} onDone={() => setEditing(false)} />
      </div>
    );
  }

  // Taking the tax-invoice PDF counts as the client having it: log it, which
  // also locks the invoice against further changes.
  const printPdf = async () => {
    try {
      await downloadInvoicePDF(inv, billSettings);
      await api.post(`/billing/invoices/${inv.id}/printed`);
      qc.invalidateQueries({ queryKey: ['invoice', invoiceId] });
      onChanged();
    } catch { toast.error('Could not build that invoice PDF'); }
  };

  const lineDisc = (l) => (l.line_discount_type
    ? fmtDiscount({ type: l.line_discount_type, value: l.line_discount_value }) : '');
  const discounted = [...inv.lines, ...(inv.items || [])].some((l) => parseFloat(l.line_discount_amount || 0) > 0);
  const sends = inv.sends || [];
  const prints = inv.prints || [];

  return (
    <div className="bg-gray-50 border-t p-4 space-y-3">
      {inv.editable && inv.status === 'unpaid' && (
        <div className="flex flex-wrap items-center gap-3 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 text-xs text-amber-800">
          <AlertTriangle size={14} className="text-amber-500 flex-shrink-0" />
          <span className="flex-1">Not printed or emailed yet — this invoice can still be changed (wrong weight, parcel added or removed).</span>
          {can('billing.invoice') && (
            <button onClick={() => setEditing(true)}
              className="px-3 py-1.5 rounded-lg text-xs font-semibold bg-white border border-amber-300 text-amber-800 hover:bg-amber-100">
              Edit invoice
            </button>
          )}
        </div>
      )}
      {!inv.editable && inv.status === 'unpaid' && inv.lock_reason && (
        <p className="text-[11px] text-gray-400">Fixed — {inv.lock_reason}. Correct it with a credit note, or cancel and re-issue.</p>
      )}
      <div className="flex flex-wrap gap-6 text-xs text-gray-600">
        {parseFloat(inv.discount_amount || 0) > 0 && (
          <span>Before discount: <strong>{Rand(parseFloat(inv.total) + parseFloat(inv.discount_amount))}</strong></span>
        )}
        {parseFloat(inv.discount_amount || 0) > 0 && (
          <span>Discount{inv.discount_note ? ` (${inv.discount_note})` : ''}: <strong className="text-red-600">−{Rand(inv.discount_amount)}</strong></span>
        )}
        <span>Subtotal (excl. VAT): <strong>{Rand(inv.subtotal)}</strong></span>
        <span>VAT {parseFloat(inv.vat_rate).toFixed(0)}%: <strong>{Rand(inv.vat_amount)}</strong></span>
        <span>Total: <strong>{Rand(inv.total)}</strong></span>
        <span>Paid: <strong className="text-green-600">{Rand(inv.paid_amount)}</strong></span>
        <span>Balance: <strong className={parseFloat(inv.balance) > 0 ? 'text-amber-600' : 'text-green-600'}>{Rand(inv.balance)}</strong></span>
        {inv.due_date && <span>Due: <strong>{new Date(inv.due_date).toLocaleDateString('en-ZA')}</strong></span>}
      </div>

      {/* What happened to this invoice, and who did it */}
      <div className="bg-white rounded-lg border px-3 py-2 text-xs text-gray-600 space-y-0.5">
        <p><span className="inline-block w-16 text-gray-400">Issued</span> {dmyt(inv.issued_at || inv.created_at)}{inv.issued_by_name || inv.created_by_name ? ` by ${inv.issued_by_name || inv.created_by_name}` : ''}</p>
        {sends.length === 0
          ? <p><span className="inline-block w-16 text-gray-400">Emailed</span> <span className="text-amber-600">Not sent yet</span></p>
          : sends.map((s) => (
            <p key={s.id}>
              <span className="inline-block w-16 text-gray-400">{String(s.sent_to).startsWith('whatsapp:') ? 'WhatsApp' : 'Emailed'}</span>
              {dmyt(s.sent_at)} to {String(s.sent_to).replace(/^whatsapp:/, '')}{s.sent_by_name ? ` by ${s.sent_by_name}` : ''}
              {s.status === 'failed' && <span className="text-red-600"> — failed{s.error ? `: ${s.error}` : ''}</span>}
            </p>
          ))}
        {prints.map((p) => (
          <p key={p.id}>
            <span className="inline-block w-16 text-gray-400">Printed</span>
            {dmyt(p.printed_at)}{p.printed_by_name ? ` by ${p.printed_by_name}` : ''}
          </p>
        ))}
        {inv.status === 'cancelled' && <p><span className="inline-block w-16 text-gray-400">Status</span> Cancelled</p>}
      </div>

      <div>
        <p className="text-xs font-semibold text-gray-500 uppercase mb-1">Parcels on this invoice</p>
        <div className="bg-white rounded-lg border divide-y">
          {inv.lines.map((l) => (
            <div key={l.tracking_number} className="flex justify-between gap-3 px-3 py-1.5 text-xs">
              <span>
                <a href={`/parcels/${l.tracking_number}`} className="font-mono font-semibold text-orange-500 hover:underline">{l.tracking_number}</a>
                <span className="text-gray-500"> · {l.sender_name} → {l.recipient_name}</span>
                {l.description ? <span className="text-gray-400"> · {l.description}</span> : null}
              </span>
              <span className="whitespace-nowrap">
                {discounted && <span className="text-gray-400 mr-2">{Rand(l.price)}{lineDisc(l) ? ` − ${lineDisc(l)}` : ''}</span>}
                <span className="font-semibold">{Rand(l.net_amount ?? l.price)}</span>
              </span>
            </div>
          ))}
        </div>
      </div>

      {inv.items?.length > 0 && (
        <div>
          <p className="text-xs font-semibold text-gray-500 uppercase mb-1">Extra items</p>
          <div className="bg-white rounded-lg border divide-y">
            {inv.items.map((it) => (
              <div key={it.id} className="flex justify-between gap-3 px-3 py-1.5 text-xs">
                <span>{it.description}<span className="text-gray-400"> · {parseFloat(it.quantity)} × {Rand(it.unit_price)}</span></span>
                <span className="whitespace-nowrap">
                  {discounted && <span className="text-gray-400 mr-2">{Rand(it.amount)}{lineDisc(it) ? ` − ${lineDisc(it)}` : ''}</span>}
                  <span className="font-semibold">{Rand(it.net_amount ?? it.amount)}</span>
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {inv.payments.length > 0 && (
        <div>
          <p className="text-xs font-semibold text-gray-500 uppercase mb-1">Payments</p>
          <div className="bg-white rounded-lg border divide-y">
            {inv.payments.map((p) => (
              <div key={p.id} className="flex items-center justify-between px-3 py-1.5 text-xs">
                <span className="text-gray-600">
                  {new Date(p.paid_at).toLocaleDateString('en-ZA')} ·{' '}
                  {p.method === 'eft' ? 'EFT (Capitec)' : p.method === 'card' ? 'Card (Capitec machine)' : 'Cash'}
                  {p.reference ? ` · ref ${p.reference}` : ''}
                  {p.recorded_by ? ` · by ${p.recorded_by}` : ''}
                </span>
                <span className="flex items-center gap-3">
                  <span className="font-semibold text-green-600">{Rand(p.amount)}</span>
                  {can('billing.reverse') && (
                    <button onClick={() => removePayment(p.id)} className="text-gray-300 hover:text-red-500" title="Delete payment">
                      <X size={13} />
                    </button>
                  )}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {inv.credit_notes?.length > 0 && (
        <div>
          <p className="text-xs font-semibold text-gray-500 uppercase mb-1">Credit notes</p>
          <div className="bg-white rounded-lg border divide-y">
            {inv.credit_notes.map((cn) => (
              <div key={cn.id} className="flex items-center justify-between px-3 py-1.5 text-xs">
                <span className="text-gray-600">
                  <span className="font-mono font-semibold text-purple-700">{cn.credit_note_number}</span>
                  {' · '}{new Date(cn.created_at).toLocaleDateString('en-ZA')}
                  {' · '}{cn.reason}
                  {cn.released_parcels ? ' · parcels released' : ''}
                  {cn.created_by_name ? ` · by ${cn.created_by_name}` : ''}
                </span>
                <span className="flex items-center gap-3">
                  <span className="font-semibold text-purple-700">− {Rand(cn.total)}</span>
                  <button onClick={() => downloadCreditNotePDF(cn, inv, billSettings).catch(() => toast.error('Could not build that credit note PDF'))}
                    title="Credit note PDF for the client" className="text-gray-300 hover:text-purple-600">
                    <FileText size={13} />
                  </button>
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {previewOpen && (
        <InvoicePreviewModal invoiceId={inv.id} onClose={() => setPreviewOpen(false)} onDownloaded={() => onChanged?.()} />
      )}
      <div className="flex flex-wrap gap-2">
        <button onClick={() => setPreviewOpen(true)} title="Look at the invoice here without downloading it"
          className="flex items-center gap-1.5 px-4 py-2 bg-white border border-gray-300 text-gray-700 hover:bg-gray-50 rounded-lg text-xs font-semibold">
          <Eye size={13} /> Preview
        </button>
        <button onClick={printPdf}
          className="flex items-center gap-1.5 px-4 py-2 bg-orange-500 hover:bg-orange-600 text-white rounded-lg text-xs font-semibold">
          <FileText size={13} /> Tax Invoice PDF
        </button>
        {inv.status !== 'cancelled' && can('billing.invoice') && (
          <button onClick={() => onEmail?.(inv)}
            className="flex items-center gap-1.5 px-4 py-2 bg-white border border-gray-300 text-gray-700 hover:bg-gray-50 rounded-lg text-xs font-semibold">
            <Mail size={13} /> {sends.some((s) => s.status === 'sent' && !String(s.sent_to).startsWith('whatsapp:')) ? 'Email again' : 'Email to client'}
          </button>
        )}
        {inv.status !== 'cancelled' && can('billing.invoice') && (
          <button onClick={() => onWhatsapp?.(inv)}
            title="Open WhatsApp with the invoice PDF link and the live statement link"
            className="flex items-center gap-1.5 px-4 py-2 bg-white border border-green-300 text-green-700 hover:bg-green-50 rounded-lg text-xs font-semibold">
            <Send size={13} /> WhatsApp to client
          </button>
        )}
        {inv.status !== 'cancelled' && can('billing.invoice') && parseFloat(inv.credited_amount || 0) < parseFloat(inv.total) - 0.005 && (
          <button onClick={() => onCredit?.(inv)}
            title="Correct this invoice by issuing a credit note against it"
            className="flex items-center gap-1.5 px-4 py-2 bg-white border border-purple-300 text-purple-700 hover:bg-purple-50 rounded-lg text-xs font-semibold">
            <Undo2 size={13} /> Credit this invoice
          </button>
        )}
      </div>
    </div>
  );
}

export function TabInvoices() {
  const qc = useQueryClient();
  const [searchParams] = useSearchParams();   // /invoices?client=…&status=unpaid from a client's page
  const [filterClient, setFilterClient] = useState(searchParams.get('client') || '');
  const [filterStatus, setFilterStatus] = useState(searchParams.get('status') || '');
  const [creating,     setCreating]     = useState(false);
  const [newClient,    setNewClient]    = useState('');
  const [picked,       setPicked]       = useState({});
  const [parcelDiscounts, setParcelDiscounts] = useState({});
  const [parcelPrices, setParcelPrices] = useState({});     // re-priced parcels on the new invoice
  const [items,        setItems]        = useState([]);     // extra items on the new invoice
  const [discountNote, setDiscountNote] = useState('');
  const [issueNow,     setIssueNow]     = useState(false);
  const [payFor,       setPayFor]       = useState(null);   // invoice being paid
  const [creditInv,    setCreditInv]    = useState(null);   // invoice being credited
  const [emailInv,     setEmailInv]     = useState(null);   // invoice being emailed
  const [waInv,        setWaInv]        = useState(null);   // invoice being WhatsApped
  const [openId,       setOpenId]       = useState(searchParams.get('open') || null);   // expanded invoice row (?open=id from "Create & open")
  const [busy,         setBusy]         = useState(false);

  const { data: clients = [] } = useQuery({
    queryKey: ['clients-all'],
    queryFn: () => api.get('/clients').then((r) => r.data),
  });
  const { data: billSettings } = useQuery({
    queryKey: ['billing-settings'],
    queryFn: () => api.get('/settings/billing').then((r) => r.data),
  });
  // Free-text search: invoice number, QuickBooks number, the billed client, a
  // tracking number, or the sender / recipient of any parcel on the invoice.
  const [search, setSearch] = useState(searchParams.get('search') || '');
  const [searchLive, setSearchLive] = useState(search);
  useEffect(() => { const t = setTimeout(() => setSearchLive(search.trim()), 350); return () => clearTimeout(t); }, [search]);
  const { data: invoices = [], isLoading } = useQuery({
    queryKey: ['invoices', filterClient, filterStatus, searchLive],
    queryFn: () => api.get('/billing/invoices', {
      params: { client_id: filterClient || undefined, status: filterStatus || undefined, search: searchLive || undefined },
    }).then((r) => r.data),
  });
  const { data: uninvoiced = [] } = useQuery({
    queryKey: ['uninvoiced', newClient],
    queryFn: () => api.get('/billing/uninvoiced', { params: { client_id: newClient } }).then((r) => r.data),
    enabled: creating && !!newClient,
  });

  const pickedList = uninvoiced.filter((p) => picked[p.tracking_number]);
  const apiItems   = itemsForApi(items);
  const resetNew = () => { setNewClient(''); setPicked({}); setParcelDiscounts({}); setParcelPrices({}); setItems([]); setDiscountNote(''); setIssueNow(false); };
  const refreshAll = () => {
    qc.invalidateQueries({ queryKey: ['invoices'] });
    qc.invalidateQueries({ queryKey: ['uninvoiced'] });
    qc.invalidateQueries({ queryKey: ['ready-to-invoice'] });
    qc.invalidateQueries({ queryKey: ['summary'] });
  };

  const createInvoice = async () => {
    if (!pickedList.length && !apiItems.length) { toast.error('Tick at least one parcel or add an item'); return; }
    setBusy(true);
    try {
      const tracking = pickedList.map((p) => p.tracking_number);
      const { data } = await api.post('/billing/invoices', {
        client_id: newClient,
        tracking_numbers: tracking,
        items: apiItems,
        parcel_discounts: parcelDiscountsForApi(parcelDiscounts, tracking),
        parcel_prices: parcelPricesForApi(parcelPrices, tracking),
        discount_note: discountNote,
        issue_now: issueNow,
      });
      toast.success(issueNow ? `Invoice ${data.invoice_number} issued` : 'Draft saved — open it to check, then Issue');
      setCreating(false); resetNew();
      refreshAll();
      setOpenId(data.id);
    } catch (err) {
      toast.error(err.response?.data?.error || 'Invoice creation failed');
    } finally {
      setBusy(false);
    }
  };

  const issueInvoice = async (inv) => {
    try {
      const { data } = await api.post(`/billing/invoices/${inv.id}/issue`);
      toast.success(`Issued as ${data.invoice_number}`);
      refreshAll();
      qc.invalidateQueries({ queryKey: ['invoice', inv.id] });
    } catch (err) {
      toast.error(err.response?.data?.error || 'Could not issue the invoice');
    }
  };

  const deleteDraft = async (inv) => {
    if (!window.confirm('Delete this draft? Its parcels go back to the invoiceable pool. Nothing is numbered yet, so no gap is left.')) return;
    try {
      await api.delete(`/billing/invoices/${inv.id}`);
      toast.success('Draft deleted');
      if (openId === inv.id) setOpenId(null);
      refreshAll();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Could not delete the draft');
    }
  };

  const cancelInvoice = async (inv) => {
    if (!window.confirm(`Cancel invoice ${inv.invoice_number}? Its parcels become invoiceable again. This keeps a gap in your numbering, so only do this for mistakes.`)) return;
    try {
      await api.post(`/billing/invoices/${inv.id}/cancel`);
      toast.success('Invoice cancelled');
      refreshAll();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Cancel failed');
    }
  };

  // Download the tax invoice from a list row: needs the full invoice (lines),
  // and the print is logged so the invoice locks like an emailed one.
  const printRow = async (inv) => {
    try {
      const { data: full } = await api.get(`/billing/invoices/${inv.id}`);
      await downloadInvoicePDF(full, billSettings);
      if (inv.status !== 'draft') {
        await api.post(`/billing/invoices/${inv.id}/printed`);
        qc.invalidateQueries({ queryKey: ['invoices'] });
        qc.invalidateQueries({ queryKey: ['invoice', inv.id] });
      }
    } catch (err) {
      toast.error(err.response?.data?.error || 'Could not build that invoice PDF');
    }
  };

  const sentCell = (inv) => {
    if (inv.status === 'draft' || inv.status === 'cancelled') return <span className="text-gray-300">—</span>;
    const printed = inv.print_count > 0
      ? <span className="text-gray-500" title={`last printed ${new Date(inv.last_printed_at).toLocaleDateString('en-ZA')}`}> · printed ×{inv.print_count}</span>
      : null;
    if (!inv.last_sent_at) return <span className="text-amber-600">Not sent{printed}</span>;
    const when = new Date(inv.last_sent_at).toLocaleDateString('en-ZA', { day: 'numeric', month: 'short' });
    return (
      <span className={inv.last_send_status === 'failed' || inv.last_send_status === 'bounced' ? 'text-red-600 font-semibold' : 'text-green-700'} title={`${inv.last_sent_to}${inv.send_count > 1 ? ` · ${inv.send_count} sends` : ''}`}>
        {inv.last_send_status === 'failed' ? 'Failed ' : inv.last_send_status === 'bounced' ? '⚠ Bounced ' : 'Sent '}{when}{inv.last_sent_by_name ? ` · ${inv.last_sent_by_name}` : ''}{printed}
      </span>
    );
  };

  return (
    <div className="space-y-4">
      <Explain>
        <strong>Invoices with VAT, and the payments against them.</strong> Every invoice starts as a
        <strong> draft</strong>: tick a client&apos;s delivered parcels, add extra items such as Fastway labels,
        set a discount on any line (or on all of them), and save. A draft has no number and can be changed
        freely. Press <strong>Issue</strong> to take the next invoice number and lock it — that is the tax
        invoice. Then <strong>Email</strong> it (the send is logged with the date and who sent it) and record
        each payment until it shows <strong>Paid</strong>. An issued invoice can still be corrected until its PDF
        is printed or it is emailed; after that fix a mistake with a credit note, or an admin can cancel it and you make a new one.
      </Explain>

      {payFor && (
        <PaymentModal invoice={payFor} onClose={() => setPayFor(null)}
          onSaved={() => { setPayFor(null); qc.invalidateQueries({ queryKey: ['invoices'] }); qc.invalidateQueries({ queryKey: ['invoice', payFor.id] }); }} />
      )}

      {creditInv && (
        <CreditNoteModal invoice={creditInv} onClose={() => setCreditInv(null)}
          onSaved={() => {
            setCreditInv(null);
            qc.invalidateQueries({ queryKey: ['invoice', creditInv.id] });
            refreshAll();
          }} />
      )}

      {emailInv && (
        <EmailInvoiceModal invoice={emailInv} settings={billSettings} onClose={() => setEmailInv(null)}
          onSent={() => { qc.invalidateQueries({ queryKey: ['invoices'] }); qc.invalidateQueries({ queryKey: ['invoice', emailInv.id] }); }} />
      )}
      {waInv && (
        <WhatsappInvoiceModal invoice={waInv} settings={billSettings} onClose={() => setWaInv(null)}
          onSent={() => { qc.invalidateQueries({ queryKey: ['invoices'] }); qc.invalidateQueries({ queryKey: ['invoice', waInv.id] }); }} />
      )}

      {/* Toolbar */}
      <div className="bg-white rounded-xl shadow-sm p-4 flex flex-wrap items-end gap-3">
        <div className="flex-1 min-w-64">
          <label className="block text-xs font-medium text-gray-500 mb-1">Search</label>
          <div className="relative">
            <input type="text" value={search} onChange={(e) => setSearch(e.target.value)}
              placeholder="Invoice no., QuickBooks no., client, tracking no., sender or recipient…"
              className="w-full border rounded-lg pl-3 pr-8 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-orange-400" />
            {search && (
              <button type="button" onClick={() => setSearch('')} className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"><X size={14} /></button>
            )}
          </div>
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-500 mb-1">Client</label>
          <select value={filterClient} onChange={(e) => setFilterClient(e.target.value)}
            className="border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-orange-400">
            <option value="">All clients</option>
            {clients.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-500 mb-1">Status</label>
          <select value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)}
            className="border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-orange-400">
            <option value="">All</option>
            <option value="draft">Draft</option>
            <option value="unpaid">Unpaid</option>
            <option value="part_paid">Part-paid</option>
            <option value="paid">Paid</option>
            <option value="cancelled">Cancelled</option>
          </select>
        </div>
        {can('billing.invoice') && <button onClick={() => { setCreating((c) => !c); resetNew(); }}
          className="ml-auto flex items-center gap-1.5 bg-orange-500 hover:bg-orange-600 text-white px-4 py-2 rounded-lg text-sm font-semibold">
          <Plus size={15} /> New Invoice
        </button>}
      </div>

      {/* Create invoice panel */}
      {creating && (
        <div className="bg-white rounded-xl shadow-sm p-5 border-2 border-orange-200 space-y-3">
          <h3 className="font-semibold text-gray-800">New invoice</h3>
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">Who is this invoice for? (the payer)</label>
            <select value={newClient} onChange={(e) => { setNewClient(e.target.value); setPicked({}); setParcelDiscounts({}); setParcelPrices({}); }}
              className="border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-orange-400 min-w-64">
              <option value="">— Select client —</option>
              {clients.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </div>

          {newClient && (
            <>
              <InvoiceLinesEditor parcels={uninvoiced} picked={picked} setPicked={setPicked}
                parcelDiscounts={parcelDiscounts} setParcelDiscounts={setParcelDiscounts}
                parcelPrices={parcelPrices} setParcelPrices={setParcelPrices}
                items={items} setItems={setItems} discountNote={discountNote} setDiscountNote={setDiscountNote}
                vatRate={parseFloat(billSettings?.billing_vat_rate) || 15}
                onPreview={() => {
                  const c = clients.find((x) => x.id === newClient) || {};
                  return openInvoicePreview(
                    { client_name: c.name, client_vat_number: c.vat_number, client_phone: c.phone, client_address: c.address },
                    { parcels: uninvoiced.filter((p) => picked[p.tracking_number]), parcelDiscounts, parcelPrices, items, discountNote,
                      vatRate: parseFloat(billSettings?.billing_vat_rate) || 15 }, billSettings);
                }} />

              <div className="flex flex-wrap items-center justify-between gap-3 bg-orange-50 rounded-lg px-4 py-3">
                <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
                  <input type="checkbox" checked={issueNow} onChange={(e) => setIssueNow(e.target.checked)} className="accent-orange-500" />
                  Issue immediately (walk-in — no draft to check first)
                </label>
                <button onClick={createInvoice} disabled={busy || (!pickedList.length && !apiItems.length)}
                  className="bg-orange-500 hover:bg-orange-600 text-white px-5 py-2 rounded-lg text-sm font-bold disabled:opacity-50">
                  {busy ? 'Saving…' : issueNow ? 'Create & issue invoice' : 'Save draft'}
                </button>
              </div>
            </>
          )}
        </div>
      )}

      {/* Invoice list */}
      <div className="bg-white rounded-xl shadow-sm overflow-hidden">
        {isLoading ? <div className="p-8 text-center text-gray-400">Loading…</div>
        : invoices.length === 0 ? <div className="p-8 text-center text-gray-400">No invoices yet — click <strong>New Invoice</strong> to create the first one</div>
        : (
          <>
          {/* Phone: one card per invoice; tap the number to open the detail */}
          <div className="md:hidden divide-y">
            {invoices.map((inv) => {
              const st = INVOICE_STATUS[inv.status] || INVOICE_STATUS.unpaid;
              const open = openId === inv.id;
              const isDraft = inv.status === 'draft';
              return (
                <div key={inv.id}>
                  <div className={`p-3 space-y-1.5 ${isDraft ? 'bg-gray-50/60' : ''}`}>
                    <div className="flex items-center justify-between gap-2">
                      <button onClick={() => setOpenId(open ? null : inv.id)} className="flex items-center gap-1 font-mono text-sm font-bold text-gray-800">
                        {open ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
                        {inv.invoice_number || <span className="font-sans font-normal italic text-gray-500">Draft</span>}
                      </button>
                      <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${st.cls}`}>{st.label}</span>
                    </div>
                    <p className="text-sm text-gray-700 truncate">{inv.client_name}</p>
                    <div className="flex flex-wrap items-center gap-x-4 gap-y-0.5 text-xs text-gray-500">
                      <span>{new Date(inv.issued_at || inv.created_at).toLocaleDateString('en-ZA')}</span>
                      <span>Total <strong className="text-gray-800">{Rand(inv.total)}</strong></span>
                      {!isDraft && <span>Balance <strong className={parseFloat(inv.balance) > 0 && inv.status !== 'cancelled' ? 'text-amber-600' : 'text-gray-500'}>{Rand(inv.balance)}</strong></span>}
                      {!isDraft && <span>{sentCell(inv)}</span>}
                    </div>
                    <div className="flex flex-wrap gap-2 pt-1">
                      {isDraft ? (can('billing.invoice') && (
                        <>
                          <button onClick={() => issueInvoice(inv)} className="text-xs font-semibold text-orange-600 border border-orange-200 px-3 py-1.5 rounded-lg">Issue</button>
                          <button onClick={() => printRow(inv)} className="text-xs text-gray-500 border px-3 py-1.5 rounded-lg">🖨 Preview</button>
                          <button onClick={() => deleteDraft(inv)} className="text-xs text-gray-500 border px-3 py-1.5 rounded-lg">Delete</button>
                        </>
                      )) : (
                        <>
                          {inv.status !== 'cancelled' && (
                            <button onClick={() => printRow(inv)} className="text-xs font-semibold text-orange-600 border border-orange-200 px-3 py-1.5 rounded-lg">🖨 PDF</button>
                          )}
                          {inv.status !== 'cancelled' && can('billing.invoice') && (
                            <button onClick={() => setEmailInv(inv)} className="text-xs font-semibold text-gray-600 border px-3 py-1.5 rounded-lg">✉ Email</button>
                          )}
                          {inv.status !== 'paid' && inv.status !== 'cancelled' && can('billing.payments') && (
                            <button onClick={() => setPayFor(inv)} className="text-xs font-semibold text-green-600 border border-green-200 px-3 py-1.5 rounded-lg">💰 Payment</button>
                          )}
                          {can('billing.reverse') && inv.status === 'unpaid' && (
                            <button onClick={() => cancelInvoice(inv)} className="text-xs text-gray-400 px-2 py-1.5 hover:text-red-500">Cancel</button>
                          )}
                        </>
                      )}
                    </div>
                  </div>
                  {open && (
                    <InvoiceDetail invoiceId={inv.id} onChanged={refreshAll}
                      onCredit={(full) => setCreditInv(full)} onEmail={(full) => setEmailInv(full)} onWhatsapp={(full) => setWaInv(full)} onGone={() => setOpenId(null)} />
                  )}
                </div>
              );
            })}
          </div>

          <div className="hidden md:block overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 text-gray-500 text-xs uppercase">
                <tr>
                  <th className="px-4 py-3 text-left">Invoice #</th>
                  <th className="px-4 py-3 text-left">Client</th>
                  <th className="px-4 py-3 text-left">Date</th>
                  <th className="px-4 py-3 text-right">Total</th>
                  <th className="px-4 py-3 text-right">Paid</th>
                  <th className="px-4 py-3 text-right">Balance</th>
                  <th className="px-4 py-3 text-center">Status</th>
                  <th className="px-4 py-3 text-left">Emailed</th>
                  <th className="px-4 py-3 text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {invoices.map((inv) => {
                  const st = INVOICE_STATUS[inv.status] || INVOICE_STATUS.unpaid;
                  const open = openId === inv.id;
                  const isDraft = inv.status === 'draft';
                  return (
                    <React.Fragment key={inv.id}>
                      <tr className={`hover:bg-gray-50 ${isDraft ? 'bg-gray-50/60' : ''}`}>
                        <td className="px-4 py-3 font-mono text-xs font-bold text-gray-800">
                          <button onClick={() => setOpenId(open ? null : inv.id)} className="flex items-center gap-1 hover:text-orange-500">
                            {open ? <ChevronUp size={13} /> : <ChevronDown size={13} />}
                            {inv.invoice_number
                              ? <>{inv.invoice_number}{isDraft && <span className="ml-1 font-sans font-normal text-gray-400">(draft)</span>}</>
                              : <span className="font-sans font-normal italic text-gray-500">Draft</span>}
                          </button>
                        </td>
                        <td className="px-4 py-3 text-gray-700">{inv.client_name}</td>
                        <td className="px-4 py-3 text-gray-400 text-xs">{new Date(inv.issued_at || inv.created_at).toLocaleDateString('en-ZA')}</td>
                        <td className="px-4 py-3 text-right font-semibold">{Rand(inv.total)}</td>
                        <td className="px-4 py-3 text-right text-green-600">{isDraft ? '' : Rand(inv.paid_amount)}</td>
                        <td className={`px-4 py-3 text-right font-semibold ${!isDraft && parseFloat(inv.balance) > 0 && inv.status !== 'cancelled' ? 'text-amber-600' : 'text-gray-400'}`}>
                          {isDraft ? '' : Rand(inv.balance)}
                        </td>
                        <td className="px-4 py-3 text-center">
                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${st.cls}`}>{st.label}</span>
                        </td>
                        <td className="px-4 py-3 text-xs whitespace-nowrap">{sentCell(inv)}</td>
                        <td className="px-4 py-3 text-right whitespace-nowrap">
                          {isDraft ? (
                            can('billing.invoice') ? (
                              <>
                                <button onClick={() => issueInvoice(inv)}
                                  className="text-xs font-semibold text-orange-600 hover:text-orange-700 border border-orange-200 hover:bg-orange-50 px-2.5 py-1.5 rounded-lg mr-2">
                                  Issue
                                </button>
                                <button onClick={() => printRow(inv)} title="Preview the draft as a PDF (nothing is logged)"
                                  className="text-xs text-gray-500 hover:text-gray-700 border border-gray-200 hover:bg-gray-50 px-2.5 py-1.5 rounded-lg mr-2">
                                  🖨 Preview
                                </button>
                                <button onClick={() => deleteDraft(inv)} className="text-xs text-gray-400 hover:text-red-500 hover:underline">Delete</button>
                              </>
                            ) : <span className="text-xs text-gray-400">draft</span>
                          ) : (
                            <>
                              {inv.status !== 'cancelled' && (
                                <button onClick={() => printRow(inv)} title="Download the tax invoice PDF to print — this is logged and fixes the invoice"
                                  className="text-xs font-semibold text-orange-600 hover:text-orange-700 border border-orange-200 hover:bg-orange-50 px-2.5 py-1.5 rounded-lg mr-2">
                                  🖨 PDF
                                </button>
                              )}
                              {inv.status !== 'cancelled' && can('billing.invoice') && (
                                <button onClick={() => setEmailInv(inv)} title="Email the tax invoice to the client"
                                  className="text-xs font-semibold text-gray-600 hover:text-gray-800 border border-gray-200 hover:bg-gray-50 px-2.5 py-1.5 rounded-lg mr-2">
                                  ✉ Email
                                </button>
                              )}
                              {inv.status !== 'paid' && inv.status !== 'cancelled' && can('billing.payments') && (
                                <button onClick={() => setPayFor(inv)}
                                  className="text-xs font-semibold text-green-600 hover:text-green-700 border border-green-200 hover:bg-green-50 px-2.5 py-1.5 rounded-lg mr-2">
                                  💰 Payment
                                </button>
                              )}
                              {can('billing.reverse') && inv.status === 'unpaid' && (
                                <button onClick={() => cancelInvoice(inv)}
                                  className="text-xs text-gray-400 hover:text-red-500 hover:underline">Cancel</button>
                              )}
                            </>
                          )}
                        </td>
                      </tr>
                      {open && (
                        <tr>
                          <td colSpan={9} className="p-0">
                            <InvoiceDetail invoiceId={inv.id}
                              onChanged={refreshAll}
                              onCredit={(full) => setCreditInv(full)}
                              onEmail={(full) => setEmailInv(full)} onWhatsapp={(full) => setWaInv(full)}
                              onGone={() => setOpenId(null)} />
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
          </>
        )}
      </div>
    </div>
  );
}

// ── Cash-up (daily till reconciliation) ───────────────────────────────────────
// Cash-up page pieces. Defined at module level on purpose: defined inside the
// page they would be re-created on every keystroke, remounting the inputs and
// dropping focus after one character.
// Each step is a card with a number; the step that needs the worker's
// attention is highlighted so the eye goes to one thing at a time.
function CashupStep({ n, title, hint, done, active, children }) {
  return (
    <div className={`bg-white rounded-xl shadow-sm border-2 ${active ? 'border-orange-400' : done ? 'border-green-200' : 'border-transparent'}`}>
      <div className="flex items-start gap-3 px-5 pt-4 pb-2">
        <span className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-sm font-bold ${done ? 'bg-green-500 text-white' : active ? 'bg-orange-500 text-white' : 'bg-gray-200 text-gray-600'}`}>
          {done ? <Check size={16} /> : n}
        </span>
        <div className="min-w-0 flex-1">
          <h3 className="font-semibold text-gray-800">{title}</h3>
          {hint && <p className="text-xs text-gray-500 mt-0.5">{hint}</p>}
        </div>
      </div>
      <div className="px-5 pb-5">{children}</div>
    </div>
  );
}
function CashupMoneyCard({ label, value, check, color }) {
  return (
    <div className={`rounded-xl p-4 ${color}`}>
      <p className="text-xs font-medium opacity-80">{label}</p>
      <p className="text-2xl font-bold mt-0.5">{Rand(value)}</p>
      {check && <p className="text-[11px] mt-1.5 opacity-75 leading-snug">{check}</p>}
    </div>
  );
}

export function TabCashup({ initialDate } = {}) {
  const qc = useQueryClient();
  const [date,    setDate]    = useState(initialDate || new Date().toISOString().split('T')[0]);
  const [opening, setOpening] = useState('0');
  const [counted, setCounted] = useState('');
  const [notes,   setNotes]   = useState('');
  const [saving,  setSaving]  = useState(false);
  const [expAmount, setExpAmount] = useState('');
  const [expDesc,   setExpDesc]   = useState('');
  const [addingExp, setAddingExp] = useState(false);
  // Which saved cash-ups to list: this week, this month, a date range, or the
  // last 60 (the default). Week starts on Monday.
  const [view, setView] = useState('today');             // today = the day's cash-up, history = past cash-ups
  const [range, setRange] = useState('recent');          // recent | week | month | custom
  const [rangeFrom, setRangeFrom] = useState('');
  const [rangeTo, setRangeTo] = useState('');
  const rangeDates = (() => {
    const iso = (d) => d.toISOString().split('T')[0];
    const today = new Date();
    if (range === 'week') { const d = new Date(today); d.setDate(d.getDate() - ((d.getDay() + 6) % 7)); return { from: iso(d), to: iso(today) }; }
    if (range === 'month') return { from: iso(new Date(today.getFullYear(), today.getMonth(), 1)), to: iso(today) };
    if (range === 'custom') return { from: rangeFrom || undefined, to: rangeTo || undefined };
    return {};
  })();

  const { data, isLoading } = useQuery({
    queryKey: ['cashup', date],
    queryFn: () => api.get('/billing/cashup/summary', { params: { date } }).then((r) => r.data),
    staleTime: 0,
  });
  const { data: history = [] } = useQuery({
    queryKey: ['cashup-history', rangeDates.from || '', rangeDates.to || ''],
    queryFn: () => api.get('/billing/cashup/history', { params: rangeDates }).then((r) => r.data),
  });
  const removeCashup = async (h) => {
    const day = new Date(h.business_date).toLocaleDateString('en-ZA');
    if (!window.confirm(`Delete the cash-up of ${day}? The day's payments stay; only the count is removed and the day goes back to "not cashed up".`)) return;
    try {
      await api.delete(`/billing/cashup/${h.id}`);
      toast.success(`Cash-up of ${day} deleted`);
      qc.invalidateQueries({ queryKey: ['cashup-history'] });
      qc.invalidateQueries({ queryKey: ['cashup-unbalanced'] });
      qc.invalidateQueries({ queryKey: ['cashup', h.business_date.split('T')[0]] });
    } catch (err) {
      toast.error(err.response?.data?.error || 'Could not delete that cash-up');
    }
  };
  const histTotals = history.reduce((acc, h) => {
    const petty = parseFloat(h.petty_cash_total || 0);
    acc.expected += parseFloat(h.expected_cash) + parseFloat(h.opening_float) - petty;
    acc.counted += parseFloat(h.counted_cash);
    acc.variance += parseFloat(h.variance);
    acc.petty += petty;
    return acc;
  }, { expected: 0, counted: 0, variance: 0, petty: 0 });
  const { data: unbalanced = [] } = useQuery({
    queryKey: ['cashup-unbalanced'],
    queryFn: () => api.get('/billing/cashup/unbalanced').then((r) => r.data),
  });
  // Days that took money but were never counted — shown on the Past cash-ups view
  const { data: missing = [] } = useQuery({
    queryKey: ['cashup-missing', 60],
    queryFn: () => api.get('/billing/cashup/missing', { params: { days: 60 } }).then((r) => r.data),
    enabled: view === 'history',
  });

  // Pre-fill the form from a saved cash-up when the date changes
  React.useEffect(() => {
    if (data?.cashup) {
      setOpening(String(data.cashup.opening_float));
      setCounted(String(data.cashup.counted_cash));
      setNotes(data.cashup.notes || '');
    } else {
      setOpening('0'); setCounted(''); setNotes('');
    }
  }, [data?.cashup, date]);

  const t = data?.totals || {};
  const expectedCash = parseFloat(t.cash || 0);
  const pettyCash = parseFloat(data?.petty_cash_total || 0);
  // Petty-cash purchases leave the till, so the drawer should be short by them
  const expectedDrawer = (parseFloat(opening) || 0) + expectedCash - pettyCash;
  const variance = counted !== '' ? (parseFloat(counted) || 0) - expectedDrawer : null;

  const save = async () => {
    if (counted === '') { toast.error('Enter the counted cash first'); return; }
    setSaving(true);
    try {
      await api.post('/billing/cashup', {
        business_date: date, opening_float: parseFloat(opening) || 0,
        counted_cash: parseFloat(counted), notes,
      });
      toast.success('Cash-up saved');
      qc.invalidateQueries({ queryKey: ['cashup', date] });
      qc.invalidateQueries({ queryKey: ['cashup-history'] });
      qc.invalidateQueries({ queryKey: ['cashup-unbalanced'] });
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to save cash-up');
    } finally {
      setSaving(false);
    }
  };

  const addExpense = async () => {
    if (!expAmount || parseFloat(expAmount) <= 0) { toast.error('Enter the amount spent'); return; }
    if (!expDesc.trim()) { toast.error('Say what the money was spent on'); return; }
    setAddingExp(true);
    try {
      await api.post('/billing/cash-expenses', {
        expense_date: date, amount: parseFloat(expAmount), description: expDesc.trim(),
      });
      setExpAmount(''); setExpDesc('');
      qc.invalidateQueries({ queryKey: ['cashup', date] });
      toast.success('Petty cash expense added');
    } catch (err) {
      toast.error(err.response?.data?.error || 'Could not add the expense');
    } finally {
      setAddingExp(false);
    }
  };

  const deleteExpense = async (id) => {
    try {
      await api.delete(`/billing/cash-expenses/${id}`);
      qc.invalidateQueries({ queryKey: ['cashup', date] });
    } catch (err) {
      toast.error(err.response?.data?.error || 'Could not remove the expense');
    }
  };

  const saved = data?.cashup;
  const balanced = variance !== null && Math.abs(variance) < 0.005;
  const dayLabel = new Date(date + 'T00:00:00').toLocaleDateString('en-ZA', { weekday: 'long', day: 'numeric', month: 'long' });
  const isToday = date === new Date().toISOString().split('T')[0];

  return (
    <div className="space-y-4">
      {/* ── Top bar: which view, which day, where it stands ─────────────── */}
      <div className="bg-white rounded-xl shadow-sm p-3 sm:p-4 flex flex-wrap items-center gap-3">
        <div className="flex rounded-lg border border-gray-200 overflow-hidden">
          {[['today', 'Cash-up', CalendarDays], ['history', 'Past cash-ups', Receipt]].map(([k, lbl, Icon]) => (
            <button key={k} type="button" onClick={() => setView(k)}
              className={`flex items-center gap-1.5 px-4 py-2 text-sm font-medium ${view === k ? 'bg-orange-500 text-white' : 'bg-white text-gray-600 hover:bg-orange-50'}`}>
              <Icon size={15} /> {lbl}
            </button>
          ))}
        </div>
        {view === 'today' && (
          <>
            <label className="text-sm text-gray-500">Day:</label>
            <input type="date" value={date} max={new Date().toISOString().split('T')[0]} onChange={(e) => setDate(e.target.value)}
              className="border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-orange-400" />
            {!isToday && (
              <button type="button" onClick={() => setDate(new Date().toISOString().split('T')[0])} className="text-xs text-orange-600 underline">Back to today</button>
            )}
            <span className={`ml-auto inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-semibold ${saved ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-800'}`}>
              {saved
                ? <><Check size={13} /> Cashed up by {saved.cashed_up_by || 'staff'} · {new Date(saved.updated_at).toLocaleString('en-ZA', { dateStyle: 'short', timeStyle: 'short' })}</>
                : <><AlertTriangle size={13} /> Not cashed up yet</>}
            </span>
          </>
        )}
      </div>

      {view === 'history' ? (
        /* ── Past cash-ups ─────────────────────────────────────────────── */
        <>
          {/* Period + export */}
          <div className="bg-white rounded-xl shadow-sm p-4 sm:p-5">
            <div className="flex flex-wrap items-start gap-3">
              <div className="flex-1 min-w-56">
                <h3 className="font-semibold text-gray-800">Past cash-ups</h3>
                <p className="text-xs text-gray-500 mt-0.5">Every day the drawer was counted. Tap a day to open it; the office checks every short or over day.</p>
              </div>
              {history.length > 0 && (
                <ExportMenu label="Export" summary={{
                  filename: `cash-ups-${rangeDates.from || 'recent'}-${rangeDates.to || ''}`, title: 'Cash-ups',
                  subtitle: rangeDates.from ? `${rangeDates.from} to ${rangeDates.to || 'today'}` : 'last 60',
                  sections: [{ name: 'Cash-ups', columns: [
                    { header: 'Date', value: (h) => (h.business_date === 'Total' ? 'Total' : new Date(h.business_date).toLocaleDateString('en-ZA')), width: 12 },
                    { header: 'Opening float', value: (h) => parseFloat(h.opening_float || 0).toFixed(2), align: 'right', width: 12 },
                    { header: 'Cash expected', value: (h) => parseFloat(h.expected_cash || 0).toFixed(2), align: 'right', width: 12 },
                    { header: 'Petty cash', value: (h) => parseFloat(h.petty_cash_total || 0).toFixed(2), align: 'right', width: 12 },
                    { header: 'Counted', value: (h) => parseFloat(h.counted_cash || 0).toFixed(2), align: 'right', width: 12 },
                    { header: 'Variance', value: (h) => parseFloat(h.variance || 0).toFixed(2), align: 'right', width: 12 },
                    { header: 'Card', value: (h) => parseFloat(h.expected_card || 0).toFixed(2), align: 'right', width: 12 },
                    { header: 'EFT Capitec', value: (h) => parseFloat(h.expected_eft_capitec || 0).toFixed(2), align: 'right', width: 12 },
                    { header: 'By', value: (h) => h.cashed_up_by || '', width: 16 },
                    { header: 'Notes', value: (h) => h.notes || '', width: 30 },
                  ], rows: history, totals: { business_date: 'Total', opening_float: history.reduce((s, h) => s + parseFloat(h.opening_float || 0), 0),
                    expected_cash: history.reduce((s, h) => s + parseFloat(h.expected_cash || 0), 0), petty_cash_total: histTotals.petty,
                    counted_cash: histTotals.counted, variance: histTotals.variance,
                    expected_card: history.reduce((s, h) => s + parseFloat(h.expected_card || 0), 0),
                    expected_eft_capitec: history.reduce((s, h) => s + parseFloat(h.expected_eft_capitec || 0), 0), cashed_up_by: '', notes: '' } }],
                }} />
              )}
            </div>
            <div className="mt-3 flex flex-wrap items-center gap-2">
              <span className="text-xs font-medium text-gray-500 mr-1">Show:</span>
              {[['recent', 'Last 60 days'], ['week', 'This week'], ['month', 'This month'], ['custom', 'Pick dates']].map(([k, lbl]) => (
                <button key={k} type="button" onClick={() => setRange(k)}
                  className={`text-xs px-3 py-1.5 rounded-full border font-medium ${range === k ? 'bg-orange-500 text-white border-orange-500' : 'border-gray-200 text-gray-600 hover:border-orange-400'}`}>
                  {lbl}
                </button>
              ))}
              {range === 'custom' && (
                <span className="flex items-center gap-1.5 text-xs text-gray-500 ml-1">
                  <input type="date" value={rangeFrom} onChange={(e) => setRangeFrom(e.target.value)} className="border rounded-lg px-2 py-1.5 text-xs" />
                  to
                  <input type="date" value={rangeTo} onChange={(e) => setRangeTo(e.target.value)} className="border rounded-lg px-2 py-1.5 text-xs" />
                </span>
              )}
            </div>
          </div>

          {/* Period at a glance */}
          {history.length > 0 && (() => {
            const shortDays = history.filter((h) => parseFloat(h.variance) < -0.005);
            const overDays = history.filter((h) => parseFloat(h.variance) > 0.005);
            const balancedDays = history.length - shortDays.length - overDays.length;
            const shortTotal = shortDays.reduce((s, h) => s + parseFloat(h.variance), 0);
            const overTotal = overDays.reduce((s, h) => s + parseFloat(h.variance), 0);
            const Tile = ({ label, value, sub, tone }) => (
              <div className={`rounded-xl p-3 ${tone}`}>
                <p className="text-[11px] font-medium opacity-75">{label}</p>
                <p className="text-xl font-bold mt-0.5">{value}</p>
                {sub && <p className="text-[11px] opacity-70 mt-0.5">{sub}</p>}
              </div>
            );
            return (
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
                <Tile label="Cash-ups done" value={history.length} tone="bg-gray-50 text-gray-700" />
                <Tile label="Balanced" value={balancedDays} sub={`${Math.round((balancedDays / history.length) * 100)}% of days`} tone="bg-green-50 text-green-700" />
                <Tile label="Short" value={shortDays.length} sub={shortDays.length ? `−${Rand(Math.abs(shortTotal))} in total` : 'none'} tone="bg-red-50 text-red-700" />
                <Tile label="Over" value={overDays.length} sub={overDays.length ? `+${Rand(overTotal)} in total` : 'none'} tone="bg-amber-50 text-amber-700" />
                <Tile label="Cash counted" value={Rand(histTotals.counted)} sub={`should have been ${Rand(histTotals.expected)}`} tone="bg-blue-50 text-blue-700" />
                <Tile label="Petty cash out" value={Rand(histTotals.petty)} tone="bg-purple-50 text-purple-700" />
              </div>
            );
          })()}

          {/* Days that took money but were never counted */}
          {missing.length > 0 && (
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
              <p className="text-sm font-semibold text-amber-800 flex items-center gap-2">
                <AlertTriangle size={16} /> {missing.length} day{missing.length === 1 ? '' : 's'} took money but {missing.length === 1 ? 'was' : 'were'} never cashed up — tap one to do it now
              </p>
              <div className="mt-2 flex flex-wrap gap-2">
                {missing.map((m) => {
                  const dt = String(m.date).split('T')[0];
                  return (
                    <button key={dt} type="button" onClick={() => { setDate(dt); setView('today'); }}
                      className="text-xs px-2.5 py-1.5 rounded-lg border border-amber-300 bg-white hover:bg-amber-100 text-amber-900">
                      {new Date(dt + 'T00:00:00').toLocaleDateString('en-ZA', { weekday: 'short', day: '2-digit', month: 'short' })}
                      <span className="ml-1.5 font-semibold">{Rand(m.cash)} cash</span>
                      <span className="text-amber-700/70"> · {m.payment_count} payment{m.payment_count === '1' || m.payment_count === 1 ? '' : 's'}</span>
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {/* The list */}
          <div className="bg-white rounded-xl shadow-sm overflow-hidden">
            {history.length === 0 ? (
              <div className="p-10 text-center">
                <Receipt size={32} className="mx-auto text-gray-300" />
                <p className="mt-2 text-sm font-medium text-gray-600">No cash-ups {range === 'recent' ? 'saved yet' : 'in this period'}.</p>
                <p className="text-xs text-gray-400 mt-1">Every saved cash-up shows here with whether the drawer balanced.</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50 text-gray-500 text-xs uppercase">
                    <tr>
                      <th className="px-4 py-2.5 text-left">Day</th>
                      <th className="px-4 py-2.5 text-right">Should hold</th>
                      <th className="px-4 py-2.5 text-right">Counted</th>
                      <th className="px-4 py-2.5 text-left">Result</th>
                      <th className="px-4 py-2.5 text-right">Petty cash</th>
                      <th className="px-4 py-2.5 text-left">Cashed up by</th>
                      <th className="px-4 py-2.5 text-left">Note</th>
                      <th className="px-2 py-2.5" />
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {history.map((h) => {
                      const v = parseFloat(h.variance);
                      const petty = parseFloat(h.petty_cash_total || 0);
                      const dt = h.business_date.split('T')[0];
                      const d = new Date(dt + 'T00:00:00');
                      const ok = Math.abs(v) < 0.005;
                      return (
                        <tr key={h.id} className="hover:bg-orange-50/40 cursor-pointer" onClick={() => { setDate(dt); setView('today'); }}>
                          <td className="px-4 py-2.5">
                            <p className="font-medium text-gray-800">{d.toLocaleDateString('en-ZA', { day: '2-digit', month: 'short', year: 'numeric' })}</p>
                            <p className="text-[11px] text-gray-400">{d.toLocaleDateString('en-ZA', { weekday: 'long' })}</p>
                          </td>
                          <td className="px-4 py-2.5 text-right text-gray-600">{Rand(parseFloat(h.expected_cash) + parseFloat(h.opening_float) - petty)}</td>
                          <td className="px-4 py-2.5 text-right font-semibold text-gray-800">{Rand(h.counted_cash)}</td>
                          <td className="px-4 py-2.5">
                            <span className={`inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-xs font-semibold ${ok ? 'bg-green-100 text-green-700' : v < 0 ? 'bg-red-100 text-red-700' : 'bg-amber-100 text-amber-800'}`}>
                              {ok ? <><Check size={12} /> Balanced</> : v < 0 ? `Short ${Rand(Math.abs(v))}` : `Over ${Rand(v)}`}
                            </span>
                          </td>
                          <td className="px-4 py-2.5 text-right text-red-600">{petty > 0 ? `− ${Rand(petty)}` : <span className="text-gray-300">—</span>}</td>
                          <td className="px-4 py-2.5">
                            <p className="text-gray-700 text-xs">{h.cashed_up_by || '—'}</p>
                            <p className="text-[11px] text-gray-400">{h.updated_at ? new Date(h.updated_at).toLocaleTimeString('en-ZA', { hour: '2-digit', minute: '2-digit' }) : ''}</p>
                          </td>
                          <td className="px-4 py-2.5 text-gray-500 text-xs max-w-xs truncate" title={h.notes || ''}>{h.notes || <span className="text-gray-300">—</span>}</td>
                          <td className="px-2 py-2.5 text-right whitespace-nowrap">
                            <button type="button" onClick={(e) => { e.stopPropagation(); setDate(dt); setView('today'); }}
                              className="text-xs text-orange-600 hover:underline mr-2">Open</button>
                            {can('billing.reverse') && (
                              <button type="button" onClick={(e) => { e.stopPropagation(); removeCashup(h); }}
                                title="Delete this cash-up (the day's payments stay)"
                                className="text-gray-300 hover:text-red-600 align-middle"><X size={14} /></button>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                  {history.length > 1 && (
                    <tfoot className="bg-gray-50 text-sm font-semibold">
                      <tr>
                        <td className="px-4 py-2.5">{history.length} cash-ups</td>
                        <td className="px-4 py-2.5 text-right text-gray-600">{Rand(histTotals.expected)}</td>
                        <td className="px-4 py-2.5 text-right">{Rand(histTotals.counted)}</td>
                        <td className={`px-4 py-2.5 ${Math.abs(histTotals.variance) < 0.005 ? 'text-green-600' : histTotals.variance < 0 ? 'text-red-600' : 'text-amber-600'}`}>
                          {Math.abs(histTotals.variance) < 0.005 ? 'Balanced overall' : histTotals.variance < 0 ? `Short ${Rand(Math.abs(histTotals.variance))} overall` : `Over ${Rand(histTotals.variance)} overall`}
                        </td>
                        <td className="px-4 py-2.5 text-right text-red-600">{histTotals.petty > 0 ? `− ${Rand(histTotals.petty)}` : '—'}</td>
                        <td colSpan={3} />
                      </tr>
                    </tfoot>
                  )}
                </table>
              </div>
            )}
          </div>
        </>
      ) : (
        /* ── The day's cash-up, step by step ───────────────────────────── */
        <>
          <details className="bg-blue-50 border border-blue-100 rounded-xl px-4 py-3 text-sm text-blue-900">
            <summary className="cursor-pointer font-semibold flex items-center gap-2"><Info size={15} /> How the cash-up works (read once)</summary>
            <ol className="mt-2 ml-5 list-decimal space-y-1 text-blue-900/90">
              <li><strong>Check the day&apos;s money.</strong> The three amounts at the top are what the system says came in today, by how it was paid. Card must match the Capitec machine&apos;s end-of-day slip, EFT must match the bank app, cash must match the drawer.</li>
              <li><strong>Write down petty cash.</strong> Anything bought out of the till today (milk, stationery, a taxi). The drawer is allowed to be short by exactly that much.</li>
              <li><strong>Count the drawer.</strong> Count every note and coin in the drawer and type the total. Don&apos;t subtract anything — the system does the sums.</li>
              <li><strong>Save.</strong> If it says Balanced, you are done. If it says short or over, count again, then save it anyway with a note saying what you think happened. The office looks at every short or over day.</li>
            </ol>
          </details>

          {unbalanced.length > 0 && (
            <div className="bg-red-50 border border-red-200 rounded-xl p-4">
              <p className="text-sm font-semibold text-red-700 flex items-center gap-2">
                <AlertTriangle size={16} /> {unbalanced.length} recent cash-up{unbalanced.length === 1 ? '' : 's'} didn&apos;t balance — tap a day to look at it
              </p>
              <div className="mt-2 flex flex-wrap gap-2">
                {unbalanced.map((d) => {
                  const dt = d.business_date.split('T')[0];
                  const v = parseFloat(d.variance);
                  return (
                    <button key={dt} onClick={() => setDate(dt)}
                      className={`text-xs px-2.5 py-1 rounded-full border ${dt === date ? 'border-red-500 bg-white' : 'border-red-200 bg-white/60'} hover:bg-white`}>
                      {new Date(d.business_date).toLocaleDateString('en-ZA', { day: '2-digit', month: 'short' })} ·{' '}
                      <span className={v < 0 ? 'text-red-600 font-semibold' : 'text-amber-600 font-semibold'}>
                        {v < 0 ? `short R${Math.abs(v).toFixed(2)}` : `over R${v.toFixed(2)}`}
                      </span>
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {isLoading ? <div className="bg-white rounded-xl shadow-sm p-8 text-center text-gray-400">Loading…</div> : (
            <>
              <CashupStep n={1} title={`Check the money that came in on ${dayLabel}`} done={!!saved}
                hint={`${t.payment_count || 0} payment${t.payment_count === 1 ? '' : 's'} recorded in the system · R${parseFloat(t.total || 0).toFixed(2)} in total. Tick each amount off against the slip or screen named under it.`}>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                  <CashupMoneyCard label="💳 Card" value={t.card} color="bg-blue-50 text-blue-800"
                    check="Must match the Capitec card machine's end-of-day total." />
                  <CashupMoneyCard label="💵 Cash" value={t.cash} color="bg-green-50 text-green-800"
                    check="Must be in the drawer (plus the float, minus petty cash) — counted in step 3." />
                  <CashupMoneyCard label="🏦 EFT" value={t.eft_capitec} color="bg-purple-50 text-purple-800"
                    check="Must show in the Capitec bank app for today." />
                </div>
                {data?.cash_payments?.length > 0 && (
                  <details className="mt-3 text-sm">
                    <summary className="cursor-pointer text-xs font-medium text-gray-500">Which cash payments make up the {Rand(t.cash)}? ({data.cash_payments.length})</summary>
                    <table className="w-full text-sm mt-2">
                      <thead className="bg-gray-50 text-gray-500 text-xs uppercase">
                        <tr>
                          <th className="px-3 py-1.5 text-left">Invoice</th>
                          <th className="px-3 py-1.5 text-left">Client</th>
                          <th className="px-3 py-1.5 text-left">Ref</th>
                          <th className="px-3 py-1.5 text-left">Taken by</th>
                          <th className="px-3 py-1.5 text-right">Amount</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y">
                        {data.cash_payments.map((p, i) => (
                          <tr key={i}>
                            <td className="px-3 py-1.5 font-mono text-xs">{p.invoice_number}</td>
                            <td className="px-3 py-1.5 text-gray-700">{p.client_name}</td>
                            <td className="px-3 py-1.5 text-gray-400 text-xs">{p.reference || '—'}</td>
                            <td className="px-3 py-1.5 text-gray-500 text-xs">{p.recorded_by || '—'}</td>
                            <td className="px-3 py-1.5 text-right font-semibold">{Rand(p.amount)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </details>
                )}
              </CashupStep>

              <CashupStep n={2} title="Write down anything bought out of the till today" done={!!saved}
                hint="Milk, stationery, a taxi… each one is taken off what the drawer should hold, so it is not counted as missing.">
                <div className="flex flex-wrap items-end gap-3 mb-3">
                  <div className="w-32">
                    <label className="block text-xs font-medium text-gray-500 mb-1">Amount</label>
                    <input type="number" step="0.01" min="0" value={expAmount} onChange={(e) => setExpAmount(e.target.value)}
                      placeholder="0.00"
                      className="w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-orange-400" />
                  </div>
                  <div className="flex-1 min-w-56">
                    <label className="block text-xs font-medium text-gray-500 mb-1">What was it for?</label>
                    <input type="text" value={expDesc} onChange={(e) => setExpDesc(e.target.value)}
                      onKeyDown={(e) => { if (e.key === 'Enter') addExpense(); }}
                      placeholder="e.g. Milk and coffee for the office"
                      className="w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-orange-400" />
                  </div>
                  <button onClick={addExpense} disabled={addingExp}
                    className="flex items-center gap-1.5 bg-gray-700 hover:bg-gray-800 text-white px-4 py-2 rounded-lg text-sm font-medium disabled:opacity-50">
                    <Plus size={15} /> Add
                  </button>
                </div>
                {data?.expenses?.length > 0 ? (
                  <table className="w-full text-sm">
                    <tbody className="divide-y">
                      {data.expenses.map((e) => (
                        <tr key={e.id} className="hover:bg-gray-50">
                          <td className="px-3 py-2 text-gray-700">{e.description}</td>
                          <td className="px-3 py-2 text-gray-500 text-xs">{e.created_by_name || '—'}</td>
                          <td className="px-3 py-2 text-right font-semibold text-red-600">− {Rand(e.amount)}</td>
                          <td className="px-3 py-2 text-right w-8">
                            <button onClick={() => deleteExpense(e.id)} title="Remove this expense" className="text-gray-300 hover:text-red-500"><X size={15} /></button>
                          </td>
                        </tr>
                      ))}
                      <tr className="bg-gray-50 font-semibold">
                        <td className="px-3 py-2 text-gray-600" colSpan={2}>Total petty cash out of the till</td>
                        <td className="px-3 py-2 text-right text-red-600">− {Rand(pettyCash)}</td>
                        <td></td>
                      </tr>
                    </tbody>
                  </table>
                ) : (
                  <p className="text-sm text-gray-400">Nothing bought out of the till on this day. Leave this empty if that is right.</p>
                )}
              </CashupStep>

              <CashupStep n={3} title="Count the cash in the drawer and type the total" active={!saved && counted === ''} done={!!saved}
                hint="Count every note and coin, including the float. Do not take anything off — the system does the sums.">
                <div className="grid grid-cols-1 sm:grid-cols-4 gap-3 items-end">
                  <div>
                    <label className="block text-xs font-medium text-gray-500 mb-1">Float at the start of the day</label>
                    <input type="number" step="0.01" min="0" value={opening} onChange={(e) => setOpening(e.target.value)}
                      className="w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-orange-400" />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-500 mb-1">Cash the system says came in</label>
                    <div className="w-full border rounded-lg px-3 py-2 text-sm bg-gray-50 font-semibold text-gray-700">+ {Rand(expectedCash)}</div>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-500 mb-1">Petty cash out (step 2)</label>
                    <div className="w-full border rounded-lg px-3 py-2 text-sm bg-gray-50 font-semibold text-red-600">− {Rand(pettyCash)}</div>
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-orange-700 mb-1">Counted in the drawer now</label>
                    <input type="number" step="0.01" min="0" value={counted} onChange={(e) => setCounted(e.target.value)}
                      placeholder="Type the total"
                      className="w-full border-2 border-orange-400 rounded-lg px-3 py-2.5 text-lg font-bold focus:outline-none focus:ring-2 focus:ring-orange-400" />
                  </div>
                </div>
                <p className="text-xs text-gray-500 mt-2">The drawer should hold <strong>{Rand(expectedDrawer)}</strong> (float {Rand(opening)} + cash {Rand(expectedCash)}{pettyCash > 0 ? ` − petty cash ${Rand(pettyCash)}` : ''}).</p>

                {variance !== null && (
                  <div className={`mt-3 rounded-xl px-4 py-4 flex items-start gap-3 ${balanced ? 'bg-green-50 text-green-800' : variance < 0 ? 'bg-red-50 text-red-800' : 'bg-amber-50 text-amber-800'}`}>
                    <span className="text-2xl leading-none">{balanced ? '✅' : variance < 0 ? '🔻' : '🔺'}</span>
                    <div>
                      <p className="text-lg font-bold">
                        {balanced ? 'Balanced — the drawer is right.' : variance < 0 ? `SHORT by ${Rand(Math.abs(variance))}` : `OVER by ${Rand(variance)}`}
                      </p>
                      <p className="text-sm mt-0.5">
                        {balanced
                          ? 'Save it below and you are done.'
                          : variance < 0
                            ? 'Less in the drawer than there should be. Count again. Did someone give change by mistake, or buy something that is not in step 2? Then save with a note.'
                            : 'More in the drawer than there should be. Count again. Was a cash payment not recorded in the system? Then save with a note.'}
                      </p>
                    </div>
                  </div>
                )}
              </CashupStep>

              <CashupStep n={4} title={saved ? 'Saved' : 'Save the cash-up'} active={!saved && counted !== ''} done={!!saved}
                hint={saved ? `Cashed up by ${saved.cashed_up_by || 'staff'} on ${new Date(saved.updated_at).toLocaleString('en-ZA', { dateStyle: 'medium', timeStyle: 'short' })}. You can still correct it and save again.` : 'A note is only needed when it does not balance.'}>
                <div className="flex flex-wrap items-end gap-3">
                  <div className="flex-1 min-w-64">
                    <label className="block text-xs font-medium text-gray-500 mb-1">Note {variance !== null && !balanced ? <span className="text-red-600">(please say what you think happened)</span> : '(optional)'}</label>
                    <input type="text" value={notes} onChange={(e) => setNotes(e.target.value)}
                      placeholder="e.g. R20 short — gave change by mistake"
                      className="w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-orange-400" />
                  </div>
                  <button onClick={save} disabled={saving || counted === ''}
                    className={`px-6 py-2.5 rounded-lg text-sm font-semibold text-white disabled:opacity-50 ${saved ? 'bg-gray-700 hover:bg-gray-800' : 'bg-orange-500 hover:bg-orange-600'}`}>
                    {saving ? 'Saving…' : saved ? 'Save correction' : 'Save cash-up'}
                  </button>
                </div>
              </CashupStep>
            </>
          )}
        </>
      )}
    </div>
  );
}

// ── Ready to invoice ──────────────────────────────────────────────────────────
// One place answering "what can I invoice right now?" across every client, so
// nobody has to pick clients one by one to find out whether there's anything
// waiting. Delivered + priced + not yet on an invoice.
// Where a parcel is in its journey, for lists of parcels about to be billed
const CHIP_TONE = {
  delivered: 'bg-green-50 text-green-700 border-green-200',
  out_for_delivery: 'bg-amber-50 text-amber-700 border-amber-200',
  cancelled: 'bg-gray-100 text-gray-500 border-gray-200',
};
function StatusChip({ status }) {
  if (!status) return null;
  const tone = CHIP_TONE[status] || 'bg-blue-50 text-blue-700 border-blue-200';
  return <span className={`inline-block px-1.5 py-0.5 rounded text-[10px] font-semibold border ${tone}`}>{STATUS_LABELS[status] || status}</span>;
}

function TabReadyToInvoice() {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [open, setOpen] = useState({});     // client_id -> parcels expanded?
  const [excluded, setExcluded] = useState({}); // tracking_number -> left off this invoice
  const [busy, setBusy] = useState(null);

  // Parcels are billable from booking. "Delivered only" is for a month-end
  // run where the office prefers to invoice completed work — remembered here.
  const [deliveredOnly, setDeliveredOnly] = useState(() => {
    try { return localStorage.getItem('ready_delivered_only') === '1'; } catch { return false; }
  });
  useEffect(() => { try { localStorage.setItem('ready_delivered_only', deliveredOnly ? '1' : '0'); } catch { /* ignore */ } }, [deliveredOnly]);

  const { data, isLoading } = useQuery({
    queryKey: ['ready-to-invoice', deliveredOnly],
    queryFn: () => api.get('/billing/ready-to-invoice', { params: deliveredOnly ? { delivered_only: '1' } : {} }).then((r) => r.data),
    staleTime: 0,
  });

  const clients = data?.clients || [];
  const isIn = (t) => !excluded[t];
  const chosen = (g) => g.parcels.filter((p) => isIn(p.tracking_number));
  const chosenTotal = (g) => chosen(g).reduce((s, p) => s + parseFloat(p.price), 0);

  const createInvoice = async (g, open = false) => {
    const picked = chosen(g);
    if (picked.length === 0) { toast.error('Every parcel is unticked — nothing to invoice'); return; }
    setBusy(g.client_id);
    try {
      const { data: inv } = await api.post('/billing/invoices', {
        client_id: g.client_id,
        tracking_numbers: picked.map((p) => p.tracking_number),
      });
      qc.invalidateQueries({ queryKey: ['ready-to-invoice'] });
      qc.invalidateQueries({ queryKey: ['invoices'] });
      qc.invalidateQueries({ queryKey: ['summary'] });
      if (open) {
        // straight to the draft, expanded, so labels or a discount can be added and it issued
        navigate(`/invoices?client=${g.client_id}&status=draft&open=${inv.id}`);
        return;
      }
      toast.success(`Draft invoice saved for ${g.client_name} — ${Rand(inv.total)}. Open it under Invoices to add labels or a discount, then Issue.`);
    } catch (err) {
      toast.error(err.response?.data?.error || 'Could not create that invoice');
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="space-y-4">
      <Explain>
        <strong>Everything waiting to be invoiced, all clients at once.</strong> A parcel lands here as soon as
        it&apos;s <strong>booked with a price</strong> and isn&apos;t on an invoice yet — you can bill before pickup.
        Tick <strong>Delivered only</strong> to see completed work only.
        Press <strong>Create draft</strong> and a draft invoice is saved for that client with the parcels listed.
        Untick any parcel you want to hold back for next time. Then open the draft under <strong>Invoices</strong>
        to add Fastway labels or a discount, and press <strong>Issue</strong> to number and lock it.
      </Explain>

      <label className="flex items-center gap-2 text-sm text-gray-600 cursor-pointer w-fit">
        <input type="checkbox" checked={deliveredOnly} onChange={(e) => setDeliveredOnly(e.target.checked)} className="accent-orange-500" />
        Delivered only
      </label>

      {isLoading ? (
        <div className="bg-white rounded-xl shadow-sm p-8 text-center text-gray-400">Loading…</div>
      ) : clients.length === 0 ? (
        <div className="bg-white rounded-xl shadow-sm p-8 text-center">
          <Check size={28} className="mx-auto mb-2 text-green-500" />
          <p className="text-gray-600 font-medium">Nothing waiting to be invoiced.</p>
          <p className="text-xs text-gray-400 mt-1">
            Every {deliveredOnly ? 'delivered, ' : ''}priced parcel is already on an invoice. Parcels still needing a price are under
            &ldquo;Needs a price&rdquo;.
          </p>
        </div>
      ) : (
        <>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
            <SummaryCard label="Clients to invoice" value={clients.length} colorClass="bg-blue-50 text-blue-700" />
            <SummaryCard label="Parcels waiting" value={data.parcel_count} colorClass="bg-purple-50 text-purple-700" />
            <SummaryCard label="Waiting to be billed" value={Rand(data.total)} colorClass="bg-orange-50 text-orange-700" />
          </div>

          {clients.map((g) => {
            const isOpen = !!open[g.client_id];
            const keeping = chosen(g);
            const held = g.parcels.length - keeping.length;
            return (
              <div key={g.client_id} className="bg-white rounded-xl shadow-sm overflow-hidden">
                <div className="p-4 flex flex-wrap items-center gap-3">
                  <button onClick={() => setOpen((o) => ({ ...o, [g.client_id]: !isOpen }))}
                    className="flex items-center gap-2 flex-1 min-w-48 text-left">
                    {isOpen ? <ChevronUp size={16} className="text-gray-400" /> : <ChevronDown size={16} className="text-gray-400" />}
                    <div>
                      <p className="font-semibold text-gray-800">{g.client_name}</p>
                      <p className="text-xs text-gray-400">
                        {keeping.length} parcel{keeping.length === 1 ? '' : 's'} · {Rand(chosenTotal(g))}
                        {held > 0 && <span className="text-amber-600"> · {held} held back</span>}
                        {' · '}
                        {g.payment_terms === 'cod' ? 'cash on delivery' : `${g.payment_terms} days`}
                      </p>
                    </div>
                  </button>
                  <button onClick={() => createInvoice(g)} disabled={busy === g.client_id || keeping.length === 0}
                    className="flex items-center gap-2 bg-orange-500 hover:bg-orange-600 text-white px-4 py-2 rounded-lg text-sm font-semibold disabled:opacity-50">
                    <Receipt size={15} />
                    {busy === g.client_id ? 'Saving…' : `Create draft · ${Rand(chosenTotal(g))}`}
                  </button>
                  <button onClick={() => createInvoice(g, true)} disabled={busy === g.client_id || keeping.length === 0}
                    title="Create the draft and open it under Invoices"
                    className="flex items-center gap-2 bg-white border border-orange-300 text-orange-700 hover:bg-orange-50 px-4 py-2 rounded-lg text-sm font-semibold disabled:opacity-50">
                    Create &amp; open →
                  </button>
                </div>

                {isOpen && (
                  <table className="w-full text-sm border-t">
                    <thead className="bg-gray-50 text-gray-500 text-xs uppercase">
                      <tr>
                        <th className="px-4 py-2 w-10"></th>
                        <th className="px-4 py-2 text-left">Tracking</th>
                        <th className="px-4 py-2 text-left">Description</th>
                        <th className="px-4 py-2 text-left">Sender → Recipient</th>
                        <th className="px-4 py-2 text-left">Status</th>
                        <th className="px-4 py-2 text-right">Price</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {g.parcels.map((p) => (
                        <tr key={p.tracking_number} className={isIn(p.tracking_number) ? '' : 'opacity-40'}>
                          <td className="px-4 py-2">
                            <input type="checkbox" checked={isIn(p.tracking_number)}
                              title="Untick to leave this parcel off the invoice"
                              onChange={() => setExcluded((x) => ({ ...x, [p.tracking_number]: isIn(p.tracking_number) }))} />
                          </td>
                          <td className="px-4 py-2 font-mono text-xs text-gray-700">{p.tracking_number}</td>
                          <td className="px-4 py-2 text-gray-600 max-w-xs truncate">{p.description || '—'}</td>
                          <td className="px-4 py-2 text-gray-500 text-xs">
                            {p.sender_name} → {p.recipient_name}
                          </td>
                          <td className="px-4 py-2 text-xs"><StatusChip status={p.status} />{p.delivered_at && <span className="text-gray-400 ml-1">{dmy(p.delivered_at)}</span>}</td>
                          <td className="px-4 py-2 text-right font-semibold text-gray-800">{Rand(p.price)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            );
          })}
        </>
      )}
    </div>
  );
}

// ── Month-end statement run ───────────────────────────────────────────────────
// The month just gone is what you normally send, so default to it.
const previousMonth = () => {
  const d = new Date();
  d.setDate(1);
  d.setMonth(d.getMonth() - 1);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
};

export function TabMonthEnd() {
  const qc = useQueryClient();
  const [month, setMonth] = useState(previousMonth());
  const [selected, setSelected] = useState([]);
  const [busy, setBusy] = useState(null);

  const { data, isLoading } = useQuery({
    queryKey: ['statement-run', month],
    queryFn: () => api.get('/billing/statement-run', { params: { month } }).then((r) => r.data),
    staleTime: 0,
  });
  const clients = data?.clients || [];
  const emailable = clients.filter((c) => c.email);

  // Clear picks when the month changes — they refer to that month's list
  React.useEffect(() => { setSelected([]); }, [month]);

  const toggle = (id) =>
    setSelected((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));

  // The preview endpoint is staff-only, so fetch it as a blob rather than
  // linking straight to it (a plain link carries no auth header).
  const preview = async (c) => {
    setBusy(c.id);
    try {
      const res = await api.get('/billing/statements/preview', {
        params: { client_id: c.id, month }, responseType: 'blob',
      });
      const url = URL.createObjectURL(res.data);
      window.open(url, '_blank');
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch {
      toast.error(`Could not build ${c.name}'s statement`);
    } finally {
      setBusy(null);
    }
  };

  const sendOne = async (c) => {
    setBusy(c.id);
    try {
      const { data: r } = await api.post('/billing/statements/send', { client_id: c.id, month });
      toast.success(`Statement emailed to ${r.sent_to}`);
      qc.invalidateQueries({ queryKey: ['statement-run', month] });
    } catch (err) {
      toast.error(err.response?.data?.error || `Could not email ${c.name}`);
    } finally {
      setBusy(null);
    }
  };

  const sendSelected = async () => {
    setBusy('bulk');
    let ok = 0; const failed = [];
    for (const id of selected) {
      const c = clients.find((x) => x.id === id);
      try {
        await api.post('/billing/statements/send', { client_id: id, month });
        ok++;
      } catch (err) {
        failed.push(c?.name || 'a client');
      }
    }
    if (ok) toast.success(`Emailed ${ok} statement${ok === 1 ? '' : 's'}`);
    if (failed.length) toast.error(`Failed for: ${failed.join(', ')}`);
    setSelected([]);
    qc.invalidateQueries({ queryKey: ['statement-run', month] });
    setBusy(null);
  };

  return (
    <div className="space-y-4">
      <Explain>
        <strong>Month-end statements.</strong> Pick a month and you&apos;ll see every client who had
        activity in it — invoices raised, payments received, or both. Each statement shows their
        balance brought forward, that month&apos;s charges and payments, and the closing balance.
        <strong> Preview</strong> to check one first, then <strong>Send</strong> to email it. Nothing
        goes out until you send it.
      </Explain>

      <div className="bg-white rounded-xl shadow-sm p-4 flex flex-wrap items-center gap-3">
        <CalendarDays size={16} className="text-orange-500" />
        <label className="text-sm font-medium text-gray-600">Statement month:</label>
        <input type="month" value={month} onChange={(e) => setMonth(e.target.value)}
          className="border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-orange-400" />
        <span className="text-xs text-gray-400">
          {clients.length} client{clients.length === 1 ? '' : 's'} with activity
          {clients.length !== emailable.length && ` · ${clients.length - emailable.length} with no email address`}
        </span>
        {selected.length > 0 && (
          <button onClick={sendSelected} disabled={busy === 'bulk'}
            className="ml-auto flex items-center gap-2 bg-orange-500 hover:bg-orange-600 text-white px-4 py-2 rounded-lg text-sm font-semibold disabled:opacity-60">
            <Send size={15} />
            {busy === 'bulk' ? 'Sending…' : `Send ${selected.length} selected`}
          </button>
        )}
      </div>

      {isLoading ? (
        <div className="bg-white rounded-xl shadow-sm p-8 text-center text-gray-400">Loading…</div>
      ) : clients.length === 0 ? (
        <div className="bg-white rounded-xl shadow-sm p-8 text-center text-gray-400">
          <CalendarDays size={28} className="mx-auto mb-2 text-gray-300" />
          No client had any invoices or payments in this month.
        </div>
      ) : (
        <div className="bg-white rounded-xl shadow-sm overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 text-gray-500 text-xs uppercase">
                <tr>
                  <th className="px-3 py-3 w-10">
                    <input type="checkbox"
                      checked={emailable.length > 0 && selected.length === emailable.length}
                      onChange={(e) => setSelected(e.target.checked ? emailable.map((c) => c.id) : [])}
                      title="Select everyone with an email address" />
                  </th>
                  <th className="px-3 py-3 text-left">Client</th>
                  <th className="px-3 py-3 text-right">Invoiced</th>
                  <th className="px-3 py-3 text-right">Paid</th>
                  <th className="px-3 py-3 text-right">Closing balance</th>
                  <th className="px-3 py-3 text-left">Last sent</th>
                  <th className="px-3 py-3 text-right">Action</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {clients.map((c) => {
                  const bal = parseFloat(c.closing_balance);
                  return (
                    <tr key={c.id} className="hover:bg-gray-50">
                      <td className="px-3 py-3">
                        <input type="checkbox" checked={selected.includes(c.id)}
                          disabled={!c.email} onChange={() => toggle(c.id)} />
                      </td>
                      <td className="px-3 py-3">
                        <div className="font-medium text-gray-800">{c.name}</div>
                        {c.email
                          ? <div className="text-xs text-gray-400">{c.email}</div>
                          : <div className="text-xs text-red-500">No email address — add one on the client</div>}
                      </td>
                      <td className="px-3 py-3 text-right text-gray-600">
                        {Rand(c.charged)}
                        <div className="text-[11px] text-gray-400">{c.invoice_count} invoice{c.invoice_count === '1' ? '' : 's'}</div>
                      </td>
                      <td className="px-3 py-3 text-right text-green-600">
                        {Rand(c.paid)}
                        <div className="text-[11px] text-gray-400">{c.payment_count} payment{c.payment_count === '1' ? '' : 's'}</div>
                      </td>
                      <td className={`px-3 py-3 text-right font-semibold ${bal > 0.005 ? 'text-orange-600' : 'text-green-600'}`}>
                        {Rand(bal)}
                      </td>
                      <td className="px-3 py-3 text-xs">
                        {c.last_sent_at ? (
                          c.last_send_status === 'failed'
                            ? <span className="text-red-500">Failed {new Date(c.last_sent_at).toLocaleDateString('en-ZA')}</span>
                            : <span className="text-green-600">✓ {new Date(c.last_sent_at).toLocaleDateString('en-ZA')}</span>
                        ) : <span className="text-gray-300">Not sent</span>}
                      </td>
                      <td className="px-3 py-3">
                        <div className="flex items-center justify-end gap-2">
                          <button onClick={() => preview(c)} disabled={busy === c.id}
                            title="Preview this statement"
                            className="flex items-center gap-1 bg-gray-100 text-gray-600 px-3 py-1.5 rounded-lg text-xs font-medium hover:bg-gray-200 disabled:opacity-60">
                            <FileText size={13} /> Preview
                          </button>
                          <button onClick={() => sendOne(c)} disabled={busy === c.id || !c.email}
                            title={c.email ? `Email this statement to ${c.email}` : 'This client has no email address'}
                            className="flex items-center gap-1 bg-orange-500 text-white px-3 py-1.5 rounded-lg text-xs font-medium hover:bg-orange-600 disabled:opacity-40">
                            <Mail size={13} /> Send
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

// Type-to-search invoice picker — a plain <select> is unusable once there are
// more than a handful of open invoices, and staff think in client names, not
// invoice numbers.
function InvoicePicker({ invoices, value, onChange, placeholder = 'Search client or invoice…' }) {
  const [open, setOpen] = useState(false);
  const [term, setTerm] = useState('');
  const boxRef = React.useRef(null);

  const selected = invoices.find((i) => i.id === value);

  React.useEffect(() => {
    const away = (e) => { if (boxRef.current && !boxRef.current.contains(e.target)) { setOpen(false); setTerm(''); } };
    document.addEventListener('mousedown', away);
    return () => document.removeEventListener('mousedown', away);
  }, []);

  const q = term.trim().toLowerCase();
  const matches = !q ? invoices : invoices.filter((i) =>
    (i.client_name || '').toLowerCase().includes(q) ||
    (i.invoice_number || '').toLowerCase().includes(q) ||
    String(parseFloat(i.balance).toFixed(2)).includes(q)
  );

  const pick = (id) => { onChange(id); setOpen(false); setTerm(''); };

  return (
    <div className="relative" ref={boxRef}>
      <button type="button" onClick={() => setOpen((o) => !o)}
        className="w-full border rounded-lg px-2 py-1.5 text-sm text-left flex items-center justify-between gap-1 hover:border-orange-400 focus:outline-none focus:ring-2 focus:ring-orange-400">
        <span className={selected ? 'text-gray-800 truncate' : 'text-gray-400'}>
          {selected ? `${selected.invoice_number} · ${selected.client_name} · ${Rand(selected.balance)}` : '— Choose invoice —'}
        </span>
        <ChevronDown size={14} className="text-gray-400 flex-shrink-0" />
      </button>

      {open && (
        <div className="absolute z-30 mt-1 w-full min-w-72 bg-white border rounded-lg shadow-lg">
          <div className="p-2 border-b">
            <input autoFocus value={term} onChange={(e) => setTerm(e.target.value)}
              placeholder={placeholder}
              className="w-full border rounded-md px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-orange-400" />
          </div>
          <div className="max-h-56 overflow-y-auto">
            {value && (
              <button type="button" onClick={() => pick('')}
                className="w-full text-left px-3 py-2 text-xs text-gray-400 hover:bg-gray-50">
                Clear selection
              </button>
            )}
            {matches.length === 0 ? (
              <p className="px-3 py-3 text-xs text-gray-400">No open invoice matches “{term}”.</p>
            ) : matches.map((i) => (
              <button key={i.id} type="button" onClick={() => pick(i.id)}
                className={`w-full text-left px-3 py-2 hover:bg-orange-50 ${i.id === value ? 'bg-orange-50' : ''}`}>
                <div className="text-sm text-gray-800">{i.client_name}</div>
                <div className="text-xs text-gray-400">
                  {i.invoice_number} · outstanding {Rand(i.balance)}
                </div>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// After a payment is matched by hand, offer to remember the payer so the same
// one matches itself next month. The phrase is editable — the bank's wording is
// only a guess at what identifies them.
function LearnKeywordPrompt({ offer, onClose }) {
  const qc = useQueryClient();
  const [word, setWord] = useState(offer.keyword);
  const [busy, setBusy] = useState(false);

  const save = async () => {
    const k = word.trim();
    if (k.length < 2) { toast.error('Too short to be a useful keyword'); return; }
    setBusy(true);
    try {
      const { data: acct } = await api.get(`/clients/${offer.client_id}/account`);
      const existing = acct.client.match_keywords || [];
      const { data } = await api.patch(`/clients/${offer.client_id}/keywords`, {
        match_keywords: [...existing, k],
      });
      toast.success(`Payments mentioning “${k}” will now find ${offer.client_name} on their own`);
      for (const c of data.conflicts || [])
        toast.error(`“${c.keywords.join('”, “')}” is also on ${c.client} — payments can't tell them apart.`,
          { duration: 8000 });
      qc.invalidateQueries({ queryKey: ['bank-txns'] });
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Could not save that keyword');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-start p-4 bg-black/50 overflow-y-auto">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-md p-5 m-auto my-4">
        <div className="flex items-start justify-between mb-2">
          <h2 className="font-bold text-gray-800">Remember this payer?</h2>
          <button onClick={onClose}><X size={18} className="text-gray-400" /></button>
        </div>
        <p className="text-sm text-gray-500 mb-3">
          Nothing on <strong>{offer.client_name}</strong> matched this payment, so you had to find them
          yourself. Save a word from the payment and next month it will match on its own.
        </p>
        <div className="bg-gray-50 rounded-lg px-3 py-2 mb-3">
          <p className="text-[11px] text-gray-400 uppercase">The bank said</p>
          <p className="text-xs text-gray-600 break-words">{offer.description}</p>
        </div>
        <label className="block text-xs font-medium text-gray-500 mb-1">Remember this word for {offer.client_name}</label>
        <input value={word} onChange={(e) => setWord(e.target.value)} autoFocus
          onKeyDown={(e) => { if (e.key === 'Enter') save(); }}
          className="w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-orange-400" />
        <p className="text-xs text-gray-400 mt-1">
          Keep it distinctive — something only this client&apos;s payments say. You can change it any time
          on their client page.
        </p>
        <div className="flex justify-end gap-2 mt-4">
          <button onClick={onClose} className="px-4 py-2 text-sm text-gray-500 hover:text-gray-700">Not now</button>
          <button onClick={save} disabled={busy}
            className="bg-orange-500 hover:bg-orange-600 text-white px-4 py-2 rounded-lg text-sm font-semibold disabled:opacity-50">
            {busy ? 'Saving…' : 'Remember it'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Bank Import (Capitec statement → auto-matched EFT payments) ────────────────
export function TabBankImport() {
  const qc = useQueryClient();
  const fileRef = React.useRef(null);
  const [uploading, setUploading] = useState(false);
  const [rawText, setRawText] = useState(null);
  const [showRaw, setShowRaw] = useState(false);
  const [lastImport, setLastImport] = useState(null);
  const [picks, setPicks] = useState({});   // { [txnId]: invoice_id } — manual overrides
  const [busyId, setBusyId] = useState(null);
  const [learn, setLearn] = useState(null); // offer to remember a payer we didn't recognise

  const { data: txns = [], isLoading } = useQuery({
    queryKey: ['bank-txns'],
    queryFn: () => api.get('/billing/bank-transactions', { params: { status: 'unmatched' } }).then((r) => r.data),
    staleTime: 0,
  });
  const { data: invoices = [] } = useQuery({
    queryKey: ['billing-invoices-open'],
    queryFn: () => api.get('/billing/invoices').then((r) => r.data),
  });
  const openInvoices = invoices.filter((i) => ['unpaid', 'part_paid'].includes(i.status) && parseFloat(i.balance) > 0);

  // The invoice chosen for a row: a manual pick if made, else the suggestion
  const chosen = (t) => (t.id in picks ? picks[t.id] : t.suggestion?.invoice_id || '');

  const upload = async (file) => {
    if (!file) return;
    setUploading(true);
    setRawText(null); setShowRaw(false);
    try {
      const form = new FormData();
      form.append('file', file);
      const { data } = await api.post('/billing/bank-import', form);
      setLastImport({ imported: data.imported, skipped: data.skipped, found: data.found });
      setRawText(data.raw_text || null);
      setPicks({});
      qc.invalidateQueries({ queryKey: ['bank-txns'] });
      if (data.imported === 0 && data.skipped === 0)
        toast(data.message || 'No incoming payments found in that file');
      else
        toast.success(`Imported ${data.imported} new payment${data.imported === 1 ? '' : 's'}` +
          (data.skipped ? ` · ${data.skipped} already imported` : ''));
    } catch (err) {
      toast.error(err.response?.data?.error || 'Could not read that statement');
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  const match = async (t, learn_keyword) => {
    const invoice_id = chosen(t);
    if (!invoice_id) { toast.error('Pick an invoice for this payment first'); return; }
    setBusyId(t.id);
    try {
      const { data } = await api.post(`/billing/bank-transactions/${t.id}/match`, { invoice_id, learn_keyword });
      toast.success('Payment recorded against the invoice');
      if (data.learned)
        toast.success(`Payments mentioning “${data.learned.keyword}” will now find ${data.learned.client_name} on their own`);
      // Nothing identifies this payer yet — offer to remember them
      if (data.learn_offer) setLearn({ ...data.learn_offer, description: t.description });
      qc.invalidateQueries({ queryKey: ['bank-txns'] });
      qc.invalidateQueries({ queryKey: ['billing-invoices-open'] });
      qc.invalidateQueries({ queryKey: ['invoices'] });
    } catch (err) {
      toast.error(err.response?.data?.error || 'Could not record that payment');
    } finally {
      setBusyId(null);
    }
  };

  const ignore = async (t) => {
    setBusyId(t.id);
    try {
      await api.post(`/billing/bank-transactions/${t.id}/ignore`);
      qc.invalidateQueries({ queryKey: ['bank-txns'] });
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed');
    } finally {
      setBusyId(null);
    }
  };

  // Confirm every row whose confident suggestion is still the chosen invoice
  const confident = txns.filter((t) => t.suggestion?.confident && chosen(t) === t.suggestion.invoice_id);
  const confirmAllConfident = async () => {
    setBusyId('bulk');
    let done = 0;
    for (const t of confident) {
      try {
        await api.post(`/billing/bank-transactions/${t.id}/match`, { invoice_id: t.suggestion.invoice_id });
        done++;
      } catch { /* skip failures (e.g. already paid); they stay in the list */ }
    }
    toast.success(`Recorded ${done} payment${done === 1 ? '' : 's'}`);
    qc.invalidateQueries({ queryKey: ['bank-txns'] });
    qc.invalidateQueries({ queryKey: ['billing-invoices-open'] });
    qc.invalidateQueries({ queryKey: ['invoices'] });
    setBusyId(null);
  };

  return (
    <div className="space-y-4">
      {learn && <LearnKeywordPrompt offer={learn} onClose={() => setLearn(null)} />}

      <Explain>
        <strong>Import Capitec payments.</strong> Download your statement from Capitec (PDF, or a CSV from a
        converter) and upload it here. We pull the <strong>incoming payments</strong> and suggest which open
        invoice each one settles. Check each row and press <strong>Confirm</strong> — that records an EFT
        payment against the invoice. Nothing is recorded until you confirm it.
      </Explain>

      {/* Upload control */}
      <div className="bg-white rounded-xl shadow-sm p-4 flex flex-wrap items-center gap-3">
        <input ref={fileRef} type="file" accept=".pdf,.csv" className="hidden"
          onChange={(e) => upload(e.target.files[0])} />
        <button onClick={() => fileRef.current?.click()} disabled={uploading}
          className="flex items-center gap-2 bg-orange-500 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-orange-600 disabled:opacity-60">
          <Upload size={16} />
          {uploading ? 'Reading statement…' : 'Upload Capitec statement'}
        </button>
        <span className="text-xs text-gray-400">PDF or CSV · incoming payments only</span>
        {lastImport && (
          <span className="text-xs text-gray-500 ml-auto">
            Last upload: {lastImport.found} found · <strong className="text-green-600">{lastImport.imported} new</strong>
            {lastImport.skipped ? ` · ${lastImport.skipped} already imported` : ''}
          </span>
        )}
      </div>

      {/* Raw text preview (PDF only) so we can see exactly what was read */}
      {rawText && (
        <div className="bg-white rounded-xl shadow-sm p-3">
          <button onClick={() => setShowRaw((v) => !v)}
            className="flex items-center gap-1.5 text-xs font-medium text-gray-500 hover:text-gray-700">
            {showRaw ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
            {showRaw ? 'Hide' : 'Show'} raw text read from the PDF
          </button>
          {showRaw && (
            <pre className="mt-2 max-h-64 overflow-auto text-[11px] leading-snug bg-gray-50 rounded-lg p-3 text-gray-600 whitespace-pre-wrap">{rawText}</pre>
          )}
        </div>
      )}

      {confident.length > 0 && (
        <div className="flex items-center justify-between bg-green-50 border border-green-100 rounded-xl px-4 py-3">
          <p className="text-sm text-green-900">
            <strong>{confident.length}</strong> payment{confident.length === 1 ? '' : 's'} where the client gave an
            invoice number, or a keyword and the amount agree. Anything less certain is left for you to check.
          </p>
          <button onClick={confirmAllConfident} disabled={busyId === 'bulk'}
            className="flex items-center gap-2 bg-green-600 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-green-700 disabled:opacity-60">
            <Check size={16} /> Confirm all {confident.length}
          </button>
        </div>
      )}

      {/* Unmatched transactions */}
      {isLoading ? (
        <div className="bg-white rounded-xl shadow-sm p-8 text-center text-gray-400">Loading…</div>
      ) : txns.length === 0 ? (
        <div className="bg-white rounded-xl shadow-sm p-8 text-center text-gray-400">
          <Landmark size={28} className="mx-auto mb-2 text-gray-300" />
          No payments waiting to be matched. Upload a statement to bring in new payments.
        </div>
      ) : (
        <div className="bg-white rounded-xl shadow-sm overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-gray-50 text-left text-xs text-gray-500 uppercase">
                <th className="px-4 py-3">Date</th>
                <th className="px-4 py-3">Description</th>
                <th className="px-4 py-3 text-right">Amount</th>
                <th className="px-4 py-3">Settles invoice</th>
                <th className="px-4 py-3 text-right">Action</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {txns.map((t) => {
                const s = t.suggestion;
                return (
                  <tr key={t.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3 whitespace-nowrap text-gray-600">{dmy(t.txn_date)}</td>
                    <td className="px-4 py-3 text-gray-700 max-w-xs truncate" title={t.description}>{t.description || '—'}</td>
                    <td className="px-4 py-3 text-right font-semibold text-gray-800 whitespace-nowrap">{Rand(t.amount)}</td>
                    <td className="px-4 py-3 min-w-64">
                      <InvoicePicker invoices={openInvoices} value={chosen(t)}
                        onChange={(id) => setPicks((p) => ({ ...p, [t.id]: id }))} />
                      {s && (
                        <p className={`text-[11px] mt-1 ${s.confident ? 'text-green-600' : 'text-amber-600'}`}>
                          {s.confident ? '✓' : '?'} {s.reason}
                          {/* Show the exact keyword that caused this, and link
                              to where it can be corrected */}
                          {s.matched_keyword && (
                            <>
                              {': '}
                              <Link to={`/clients/${s.client_id}`} target="_blank"
                                title={`"${s.matched_keyword}" is a keyword on ${s.client_name} — click to change it`}
                                className="underline decoration-dotted font-medium">
                                “{s.matched_keyword}”
                              </Link>
                            </>
                          )}
                        </p>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center justify-end gap-2">
                        <button onClick={() => match(t)} disabled={busyId === t.id}
                          className="flex items-center gap-1 bg-green-600 text-white px-3 py-1.5 rounded-lg text-xs font-medium hover:bg-green-700 disabled:opacity-60">
                          <Check size={13} /> Confirm
                        </button>
                        <button onClick={() => ignore(t)} disabled={busyId === t.id}
                          className="flex items-center gap-1 bg-gray-100 text-gray-600 px-3 py-1.5 rounded-lg text-xs font-medium hover:bg-gray-200 disabled:opacity-60">
                          <Ban size={13} /> Ignore
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ── Pastel export ─────────────────────────────────────────────────────────────
// A period's customers, invoices/credit notes and payments as a zip of CSVs for
// the bookkeeper to import into Sage Pastel Partner.
export function TabPastelExport() {
  const firstOfLastMonth = () => {
    const d = new Date(); d.setDate(1); d.setMonth(d.getMonth() - 1);
    return d.toISOString().split('T')[0];
  };
  const lastOfLastMonth = () => {
    const d = new Date(); d.setDate(0);
    return d.toISOString().split('T')[0];
  };
  const [from, setFrom] = useState(firstOfLastMonth());
  const [to, setTo] = useState(lastOfLastMonth());
  const [busy, setBusy] = useState(false);

  const { data } = useQuery({
    queryKey: ['pastel-summary', from, to],
    queryFn: () => api.get('/billing/pastel-export/summary', { params: { from, to } }).then((r) => r.data),
    enabled: !!from && !!to && from <= to,
    retry: false,
  });

  const nothing = data && Number(data.invoices) === 0 && Number(data.credit_notes) === 0 && Number(data.receipts) === 0;

  const download = async () => {
    setBusy(true);
    try {
      const res = await api.get('/billing/pastel-export', { params: { from, to }, responseType: 'blob' });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url; a.download = `pastel-export-${from}_${to}.zip`; a.click();
      setTimeout(() => URL.revokeObjectURL(url), 60000);
      toast.success('Pastel export downloaded');
    } catch {
      toast.error('Could not build the Pastel export');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-4">
      <Explain>
        <strong>Export a period for Pastel.</strong> Downloads a zip with three files —
        <strong> customers</strong>, <strong>sales</strong> (invoices &amp; credit notes) and
        <strong> receipts</strong> (payments) — for your bookkeeper to import into Sage Pastel Partner.
        Each client gets a fixed account code, so re-importing customers updates rather than duplicates.
        A README inside explains the import steps. Import each period once — importing twice double-posts.
      </Explain>

      {data && !data.configured && (
        <div className="flex items-start gap-2 bg-amber-50 border border-amber-200 rounded-xl px-4 py-3">
          <Info size={15} className="text-amber-600 flex-shrink-0 mt-0.5" />
          <p className="text-sm text-amber-900">
            The Pastel <strong>GL account, VAT type and cashbook codes</strong> aren&apos;t set yet, so those
            columns will be blank. Fill them under <strong>Settings → Pricing</strong>, or your bookkeeper
            can map them in Pastel. The export still works without them.
          </p>
        </div>
      )}

      <div className="bg-white rounded-xl shadow-sm p-4 flex flex-wrap items-end gap-3">
        <div>
          <label className="block text-xs font-medium text-gray-500 mb-1">From</label>
          <input type="date" value={from} onChange={(e) => setFrom(e.target.value)}
            className="border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-orange-400" />
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-500 mb-1">To</label>
          <input type="date" value={to} onChange={(e) => setTo(e.target.value)}
            className="border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-orange-400" />
        </div>
        <button onClick={download} disabled={busy || nothing}
          className="ml-auto flex items-center gap-2 bg-orange-500 hover:bg-orange-600 text-white px-4 py-2 rounded-lg text-sm font-semibold disabled:opacity-50">
          <FileDown size={15} /> {busy ? 'Building…' : 'Download for Pastel'}
        </button>
      </div>

      {data && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <SummaryCard label="Customers" value={data.clients} colorClass="bg-blue-50 text-blue-700" />
          <SummaryCard label="Invoices" value={data.invoices} colorClass="bg-gray-50 text-gray-700" />
          <SummaryCard label="Credit notes" value={data.credit_notes} colorClass="bg-purple-50 text-purple-700" />
          <SummaryCard label="Receipts" value={data.receipts} colorClass="bg-green-50 text-green-700" />
        </div>
      )}
      {nothing && (
        <p className="text-sm text-gray-400 text-center py-4">
          No invoices, credit notes or payments in this period.
        </p>
      )}
    </div>
  );
}

// ── Billing page ──────────────────────────────────────────────────────────────
// "Ready to invoice" first — it's where the billing day starts.
// "Needs a price" was called "Unbilled Parcels", which read as "not invoiced yet"
// and sent people hunting for parcels that were never going to be listed there.
// Invoices moved out to its own page (/invoices) — it was buried here, and
// taking a payment is a daily counter job that deserves its own front door.
const TABS = [
  { key: 'ready',      label: 'Ready to Invoice',    icon: Receipt,       component: TabReadyToInvoice },
  { key: 'statements', label: 'Parcel Activity',     icon: FileText,      component: TabClientStatements },
  { key: 'unbilled',   label: 'Needs a Price',       icon: AlertTriangle, component: TabUnbilled },
];

export default function Billing() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const requested = searchParams.get('tab');
  // Old links (and bookmarks) pointing at the invoices tab still work
  useEffect(() => {
    if (requested === 'invoices') navigate('/invoices', { replace: true });
  }, [requested, navigate]);
  const initial = TABS.some(t => t.key === requested) ? requested : 'ready';
  const [tab, setTab] = useState(initial);
  const Active = (TABS.find(t => t.key === tab) || TABS[0]).component;

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-3">
        <Receipt size={22} className="text-orange-500" />
        <div>
          <h1 className="text-xl font-bold text-gray-800">Billing</h1>
          <p className="text-xs text-gray-400">Invoices, payments, parcel activity and pricing</p>
        </div>
      </div>

      <div className="flex gap-1 bg-white rounded-xl shadow-sm p-1.5 flex-wrap">
        {TABS.map(({ key, label, icon: Icon }) => (
          <button key={key} onClick={() => setTab(key)}
            className={`flex items-center gap-2 flex-1 justify-center px-4 py-2 rounded-lg text-sm font-medium transition-colors min-w-28 ${
              tab === key ? 'bg-orange-500 text-white shadow-sm' : 'text-gray-600 hover:bg-gray-100'
            }`}>
            <Icon size={15} />
            {label}
          </button>
        ))}
      </div>

      <Active />
    </div>
  );
}
